import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { BrowserRouter, Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import { useI18n } from './i18n';

function normalizeApiBaseUrl(rawUrl) {
  return String(rawUrl || '').trim().replace(/\/+$/, '');
}

function resolveApiUrl() {
  const envValue = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
  if (envValue) {
    return envValue;
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:8001';
  }

  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8001';
  }

  return normalizeApiBaseUrl(`${window.location.origin}/api`);
}

const API_URL = resolveApiUrl();
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const ACCESS_TOKEN_KEY = 'asyncagent_access_token';
const MOBILE_BREAKPOINT = 980;
const ALL_VIEW_CHANNELS = [
  'chat_messages',
  'memory_events',
  'questions',
  'listeners',
  'artifacts',
  'tools',
  'tool_requests',
  'documents',
  'web_apps',
  'events',
  'llm_calls'
];
const MOBILE_TABS = [
  { id: 'home' },
  { id: 'chat' },
  { id: 'memory' },
  { id: 'work' },
  { id: 'data' }
];
const MOBILE_TAB_CHANNELS = {
  home: ['questions', 'listeners', 'tool_requests'],
  chat: ['chat_messages'],
  memory: ['memory_events', 'events', 'llm_calls'],
  work: ['artifacts'],
  data: ['documents', 'tools', 'web_apps']
};
const LOCALE_OPTIONS = [
  { id: 'fr', label: 'FR' },
  { id: 'en', label: 'EN' },
  { id: 'de', label: 'DE' }
];

function getStoredAccessToken() {
  return window.localStorage.getItem(ACCESS_TOKEN_KEY) || '';
}

function storeAccessToken(token) {
  if (token) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
    return;
  }
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function applyAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }
  delete axios.defaults.headers.common.Authorization;
}

function buildSseUrl(path, token) {
  const base = new URL(`${API_URL}${path}`);
  if (token) base.searchParams.set('access_token', token);
  return base.toString();
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function formatTokenCount(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  return new Intl.NumberFormat().format(Math.max(0, Math.round(numeric)));
}

function formatUsd(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '$0.00';
  const abs = Math.abs(numeric);
  let digits = 2;
  if (abs > 0 && abs < 1) digits = 4;
  if (abs > 0 && abs < 0.01) digits = 6;
  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}

function formatCredits(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  return new Intl.NumberFormat().format(Math.max(0, Math.floor(numeric)));
}

function userCreditsLabel(user, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  if (!user || typeof user !== 'object') return tt('credits.remaining', { value: '-' });
  if (user.credits_unlimited) return tt('credits.unlimited');
  return tt('credits.remaining', { value: formatCredits(user.credits_remaining) });
}

function compactCreditsLabel(user, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  if (!user || typeof user !== 'object') return '- cr';
  if (user.credits_unlimited) return tt('credits.shortUnlimited');
  return tt('credits.shortRemaining', { value: formatCredits(user.credits_remaining) });
}

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeArtifactType(type) {
  const value = (type || '').toLowerCase();
  if (['postit', 'sticky', 'sticky_note', 'note'].includes(value)) return 'postit';
  if (['chart', 'graph', 'plot'].includes(value)) return 'chart';
  if (['image', 'img', 'photo'].includes(value)) return 'image';
  if (['table', 'tabular'].includes(value)) return 'table';
  if (['webform', 'web_form'].includes(value)) return 'webform';
  if (['widget', 'ui_widget', 'form'].includes(value)) return 'widget';
  return 'text';
}

function inferDocTypeFromInput(value) {
  const raw = String(value || '').toLowerCase();
  if (raw.endsWith('.pdf') || raw.includes('application/pdf')) return 'pdf';
  if (
    raw.endsWith('.png') ||
    raw.endsWith('.jpg') ||
    raw.endsWith('.jpeg') ||
    raw.endsWith('.webp') ||
    raw.endsWith('.gif') ||
    raw.includes('image/')
  ) return 'image';
  if (
    raw.endsWith('.txt') ||
    raw.endsWith('.md') ||
    raw.endsWith('.csv') ||
    raw.endsWith('.json') ||
    raw.includes('text/')
  ) return 'text';
  return 'generic';
}

function normalizeDocumentType(doc) {
  const candidates = [doc?.doc_type, doc?.mime_type, doc?.name, doc?.url]
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  for (const candidate of candidates) {
    const type = inferDocTypeFromInput(candidate);
    if (type !== 'generic') return type;
  }
  return 'generic';
}

function documentTypeToken(type) {
  if (type === 'pdf') return 'PDF';
  if (type === 'image') return 'IMG';
  if (type === 'text') return 'TXT';
  return 'DOC';
}

function normalizeAnalysisStatus(status) {
  const value = String(status || 'none').toLowerCase();
  if (['ready', 'failed', 'pending', 'none'].includes(value)) return value;
  return 'pending';
}

function buildDocumentTooltip(doc, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  const lines = [];
  const status = normalizeAnalysisStatus(doc?.analysis_status);
  lines.push(`${tt('agent.documentAnalysisLabel')}: ${status}`);
  if (doc?.analysis_summary) lines.push(String(doc.analysis_summary));
  if (doc?.source) lines.push(`${tt('agent.documentSourceLabel')}: ${doc.source}`);
  if (doc?.mime_type) lines.push(`${tt('agent.documentMimeLabel')}: ${doc.mime_type}`);
  return lines.join('\n');
}

function isCaptainCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (raw.startsWith('local://')) {
    return /^[A-Za-z0-9]{8}$/.test(raw.slice('local://'.length));
  }
  return /^[A-Za-z0-9]{8}$/.test(raw);
}

function toolServerLabel(tool, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  if (tool?.tool_source === 'internal') {
    return tt('agent.nativeRuntime');
  }
  if (tool?.tool_source === 'captain_managed' && tool?.captain_tool_code) {
    return `${tt('agent.captainIdPrefix')} ${tool.captain_tool_code}`;
  }
  return tool?.mcp_server || '-';
}

function buildWebFormUrl(linkCode) {
  const code = String(linkCode || '').trim();
  if (!code) return '';
  try {
    return new URL(`/webform/${code}`, API_URL).toString();
  } catch {
    return `${API_URL}/webform/${code}`;
  }
}

function buildDocumentDownloadUrl(doc, accessToken = '') {
  const path = String(doc?.download_path || '').trim();
  if (!path) return '';
  try {
    const url = new URL(`${API_URL}${path}`);
    if (accessToken) url.searchParams.set('access_token', accessToken);
    return url.toString();
  } catch {
    const tokenSuffix = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : '';
    return `${API_URL}${path}${tokenSuffix}`;
  }
}

function buildDownloadUrlFromPath(path, accessToken = '') {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return '';
  try {
    const url = new URL(`${API_URL}${normalizedPath}`);
    if (accessToken) url.searchParams.set('access_token', accessToken);
    return url.toString();
  } catch {
    const tokenSuffix = accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : '';
    return `${API_URL}${normalizedPath}${tokenSuffix}`;
  }
}

function toolTypeLabel(tool) {
  const source = String(tool?.tool_source || '').toLowerCase();
  if (source === 'internal') return 'NAT';
  if (source === 'captain_managed') return 'CAP';
  return 'MCP';
}

function toolTypeClassName(tool) {
  const source = String(tool?.tool_source || '').toLowerCase();
  if (source === 'internal') return 'resource-token--native';
  if (source === 'captain_managed') return 'resource-token--captain';
  return '';
}

function normalizeLineItems(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .slice(0, 24);
  }
  return String(rawValue || '')
    .split(/\r?\n|[,;]+/g)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 24);
}

function instructionItemsToText(value) {
  return normalizeLineItems(value).join('\n');
}

function emptyAgentSystemInstructions() {
  return {
    chat_tone: '',
    approach_guidelines: '',
    expected_artifacts: '',
    recommended_tools: '',
    additional_instructions: ''
  };
}

function instructionsPayloadToDraft(payload) {
  if (!payload || typeof payload !== 'object') {
    return emptyAgentSystemInstructions();
  }
  return {
    chat_tone: String(payload.chat_tone || ''),
    approach_guidelines: String(payload.approach_guidelines || ''),
    expected_artifacts: instructionItemsToText(payload.expected_artifacts),
    recommended_tools: instructionItemsToText(payload.recommended_tools),
    additional_instructions: String(payload.additional_instructions || '')
  };
}

function systemInstructionsDraftToPayload(draft) {
  const safeDraft = (draft && typeof draft === 'object') ? draft : emptyAgentSystemInstructions();
  const payload = {
    chat_tone: String(safeDraft.chat_tone || '').trim(),
    approach_guidelines: String(safeDraft.approach_guidelines || '').trim(),
    expected_artifacts: normalizeLineItems(safeDraft.expected_artifacts),
    recommended_tools: normalizeLineItems(safeDraft.recommended_tools),
    additional_instructions: String(safeDraft.additional_instructions || '').trim()
  };
  if (
    !payload.chat_tone &&
    !payload.approach_guidelines &&
    payload.expected_artifacts.length === 0 &&
    payload.recommended_tools.length === 0 &&
    !payload.additional_instructions
  ) {
    return null;
  }
  return payload;
}

function AgentSystemInstructionsFields({
  draft,
  setDraft,
  disabled = false,
  t,
  className = 'advanced-create-grid'
}) {
  if (!draft || typeof draft !== 'object') return null;
  return (
    <div className={className}>
      <input
        type="text"
        value={draft.chat_tone}
        onChange={(e) => setDraft((prev) => ({ ...prev, chat_tone: e.target.value }))}
        placeholder={t('home.chatTonePlaceholder')}
        disabled={disabled}
      />
      <textarea
        value={draft.approach_guidelines}
        onChange={(e) => setDraft((prev) => ({ ...prev, approach_guidelines: e.target.value }))}
        placeholder={t('home.approachGuidelinesPlaceholder')}
        disabled={disabled}
        rows={4}
      />
      <textarea
        value={draft.expected_artifacts}
        onChange={(e) => setDraft((prev) => ({ ...prev, expected_artifacts: e.target.value }))}
        placeholder={t('home.expectedArtifactsPlaceholder')}
        disabled={disabled}
        rows={3}
      />
      <textarea
        value={draft.recommended_tools}
        onChange={(e) => setDraft((prev) => ({ ...prev, recommended_tools: e.target.value }))}
        placeholder={t('home.recommendedToolsPlaceholder')}
        disabled={disabled}
        rows={3}
      />
      <textarea
        value={draft.additional_instructions}
        onChange={(e) => setDraft((prev) => ({ ...prev, additional_instructions: e.target.value }))}
        placeholder={t('home.additionalInstructionsPlaceholder')}
        disabled={disabled}
        rows={3}
      />
    </div>
  );
}

function normalizeQuestionType(type) {
  const value = String(type || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (value === 'closed') return 'closed';
  if (value === 'multi_closed') return 'multi_closed';
  if (value === 'open') return 'open';
  return 'open';
}

function resolveQuestionInputMode(question) {
  const normalizedType = normalizeQuestionType(question?.question_type);
  const options = Array.isArray(question?.options)
    ? question.options.map((opt) => String(opt || '').trim()).filter(Boolean)
    : [];

  if (normalizedType === 'closed' && options.length >= 2) return 'closed';
  if (normalizedType === 'multi_closed' && options.length >= 2) return 'multi_closed';

  if (options.length >= 2) {
    const text = String(question?.question_text || '').toLowerCase();
    const likelyMulti = /plusieurs|multiple|multi|plus d.?une|sélectionnez|selectionnez|cocher|all that apply|one or more|select all/.test(text);
    return likelyMulti ? 'multi_closed' : 'closed';
  }

  return 'open';
}

function questionTypeLabel(question, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  const value = resolveQuestionInputMode(question);
  if (value === 'closed') return tt('agent.questionType.closed');
  if (value === 'multi_closed') return tt('agent.questionType.multiClosed');
  return tt('agent.questionType.open');
}

function truncateText(value, maxLen = 180) {
  const text = String(value || '').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 3)}...`;
}

function buildToolInputTooltip(input) {
  if (!input || typeof input !== 'object') return '';
  try {
    const text = JSON.stringify(input, null, 2);
    if (text.length <= 1600) return text;
    return `${text.slice(0, 1597)}...`;
  } catch {
    return String(input);
  }
}

function llmCallTypeLabel(value, t = null) {
  const tt = typeof t === 'function' ? t : (key) => key;
  const type = String(value || '').trim();
  if (type === 'tool_call') return tt('llm.callType.toolCall');
  if (type === 'tool_finished') return tt('llm.callType.toolFinished');
  if (type === 'round_output') return tt('llm.callType.roundOutput');
  if (type === 'captain_request') return tt('llm.callType.captainRequest');
  if (type === 'image_generation') return tt('llm.callType.imageGeneration');
  return type || '-';
}

function LanguagePicker({ className = '' }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <label className={`locale-switch ${className}`.trim()}>
      <span className="visually-hidden">{t('common.language')}</span>
      <select
        aria-label={t('common.language')}
        value={locale}
        onChange={(e) => setLocale(e.target.value)}
      >
        {LOCALE_OPTIONS.map((item) => (
          <option key={item.id} value={item.id}>{item.label}</option>
        ))}
      </select>
    </label>
  );
}

function normalizeWidgetOptionItems(rawOptions) {
  if (!Array.isArray(rawOptions)) return [];
  const options = [];
  const seenValues = new Set();
  rawOptions.forEach((option) => {
    let value = '';
    let label = '';
    if (option && typeof option === 'object' && !Array.isArray(option)) {
      value = String(
        option.value ?? option.id ?? option.key ?? option.label ?? option.name ?? ''
      ).trim();
      label = String(option.label ?? option.name ?? value).trim();
    } else {
      value = String(option ?? '').trim();
      label = value;
    }
    if (!value || seenValues.has(value)) return;
    seenValues.add(value);
    options.push({ value, label: label || value });
  });
  return options;
}

function normalizeWidgetNumericValue(value, fallback = null) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  return fallback;
}

function getWidgetNumericBounds(field, options = {}) {
  const type = String(field?.type || '').trim();
  const sliderDefaults = options.sliderDefaults === true || type === 'slider';
  const minRaw = normalizeWidgetNumericValue(field?.min, null);
  const maxRaw = normalizeWidgetNumericValue(field?.max, null);
  let min = Number.isFinite(minRaw) ? minRaw : (sliderDefaults ? 0 : null);
  let max = Number.isFinite(maxRaw) ? maxRaw : (sliderDefaults ? 100 : null);
  if (Number.isFinite(min) && Number.isFinite(max) && max < min) {
    max = sliderDefaults ? min + 1 : min;
  }
  return { min, max };
}

function normalizeWidgetColumnDefinition(rawColumn, index) {
  if (!rawColumn || typeof rawColumn !== 'object') return null;
  const rawType = String(rawColumn.type || 'text').trim().toLowerCase();
  const normalizedType = (
    rawType === 'dropdown' ? 'select'
      : rawType === 'number' ? 'decimal'
        : rawType === 'display_text' ? 'label'
        : rawType
  );
  const allowedTypes = new Set([
    'label',
    'text',
    'textarea',
    'integer',
    'decimal',
    'select',
    'multi_select',
    'radio',
    'boolean',
    'date',
    'time',
    'datetime'
  ]);
  const type = allowedTypes.has(normalizedType) ? normalizedType : 'text';
  const id = String(rawColumn.id || `col_${index + 1}`).trim();
  const label = String(rawColumn.label || id).trim();
  if (!id || !label) return null;
  const normalized = {
    id,
    type,
    label,
    required: Boolean(rawColumn.required),
    editable: type === 'label' ? false : rawColumn.editable !== false,
    placeholder: String(rawColumn.placeholder || '').trim()
  };
  if (type === 'label') {
    normalized.default = String(rawColumn.text ?? rawColumn.default ?? '').trim();
    return normalized;
  }
  if (type === 'select' || type === 'multi_select' || type === 'radio') {
    const options = normalizeWidgetOptionItems(rawColumn.options);
    if (options.length < 2) return null;
    normalized.options = options;
  }
  if (type === 'integer' || type === 'decimal') {
    const min = normalizeWidgetNumericValue(rawColumn.min);
    const max = normalizeWidgetNumericValue(rawColumn.max);
    const step = normalizeWidgetNumericValue(rawColumn.step);
    if (Number.isFinite(min)) normalized.min = type === 'integer' ? Math.round(min) : min;
    if (Number.isFinite(max)) normalized.max = type === 'integer' ? Math.round(max) : max;
    if (Number.isFinite(step) && step > 0) {
      normalized.step = type === 'integer' ? Math.max(1, Math.round(step)) : step;
    }
  }
  if (rawColumn.default !== undefined) normalized.default = rawColumn.default;
  return normalized;
}

function parseWidgetDefinition(content) {
  const parsed = safeJsonParse(content);
  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.fields)) return null;
  const allowedTypes = new Set([
    'label',
    'text',
    'textarea',
    'slider',
    'integer',
    'decimal',
    'select',
    'multi_select',
    'radio',
    'boolean',
    'date',
    'time',
    'datetime',
    'tags',
    'table_editor'
  ]);
  const seenIds = new Set();
  const fields = parsed.fields
    .filter((field) => field && typeof field === 'object')
    .map((field, index) => {
      let type = String(field.type || '').trim().toLowerCase();
      if (type === 'dropdown') type = 'select';
      if (type === 'number') type = 'decimal';
      if (type === 'display_text') type = 'label';
      const id = String(field.id || `field_${index + 1}`).trim();
      const label = String(field.label || id).trim();
      if (!id || !label || !allowedTypes.has(type) || seenIds.has(id)) return null;
      seenIds.add(id);
      const normalized = {
        id,
        type,
        label,
        required: Boolean(field.required),
        editable: type === 'label' ? false : field.editable !== false,
        placeholder: String(field.placeholder || '').trim(),
        helpText: String(field.help_text || '').trim(),
        unit: String(field.unit || '').trim()
      };
      if (type === 'select' || type === 'multi_select' || type === 'radio') {
        const options = normalizeWidgetOptionItems(field.options);
        if (options.length < 2) return null;
        normalized.options = options;
      }
      if (type === 'slider' || type === 'integer' || type === 'decimal') {
        const { min, max } = getWidgetNumericBounds(field, { sliderDefaults: type === 'slider' });
        const stepDefault = type === 'decimal' ? 0.1 : 1;
        const step = normalizeWidgetNumericValue(field.step, stepDefault);
        if (Number.isFinite(min)) normalized.min = type === 'integer' ? Math.round(min) : min;
        if (Number.isFinite(max)) normalized.max = type === 'integer' ? Math.round(max) : max;
        normalized.step = type === 'integer' ? Math.max(1, Math.round(step || 1)) : Math.max(step || 0.1, 0.0001);
      }
      if (type === 'table_editor') {
        const columns = Array.isArray(field.columns)
          ? field.columns
            .map((column, colIndex) => normalizeWidgetColumnDefinition(column, colIndex))
            .filter(Boolean)
          : [];
        if (!columns.length) return null;
        normalized.columns = columns;
        const minRows = Number(field.min_rows);
        const maxRows = Number(field.max_rows);
        if (Number.isFinite(minRows) && minRows >= 0) normalized.minRows = Math.floor(minRows);
        if (Number.isFinite(maxRows) && maxRows >= 1) normalized.maxRows = Math.floor(maxRows);
        normalized.allowRowDelete = field.allow_row_delete !== false;
      }
      normalized.default = field.default !== undefined ? field.default : field.text;
      return normalized;
    })
    .filter(Boolean);
  if (!fields.length) return null;
  const modeRaw = String(parsed.mode || '').trim().toLowerCase();
  const submitBehaviorRaw = String(parsed.submit_behavior || '').trim().toLowerCase();
  return {
    version: Number(parsed.version || 2),
    description: typeof parsed.description === 'string' ? parsed.description : '',
    mode: modeRaw === 'journal' ? 'journal' : 'snapshot',
    submitBehavior: submitBehaviorRaw === 'explicit_submit' ? 'explicit_submit' : 'auto_debounce',
    fields
  };
}

function parseWebFormArtifact(content) {
  const parsed = safeJsonParse(content);
  if (!parsed || typeof parsed !== 'object') return null;
  const linkCode = String(parsed.link_code || '').trim();
  if (!linkCode) return null;
  const appIdRaw = Number(parsed.app_id);
  const linkIdRaw = Number(parsed.link_id);
  return {
    appId: Number.isFinite(appIdRaw) && appIdRaw > 0 ? Math.round(appIdRaw) : null,
    appCode: String(parsed.app_code || '').trim(),
    linkId: Number.isFinite(linkIdRaw) && linkIdRaw > 0 ? Math.round(linkIdRaw) : null,
    linkCode,
    title: String(parsed.title || '').trim(),
    description: String(parsed.description || '').trim(),
    successMessage: String(parsed.success_message || '').trim()
  };
}

function parseWebFormFieldOptions(optionsRaw) {
  const options = [];
  const seenValues = new Set();
  if (!Array.isArray(optionsRaw)) return options;
  optionsRaw.forEach((option) => {
    let value = '';
    let label = '';
    if (option && typeof option === 'object' && !Array.isArray(option)) {
      value = String(
        option.value ?? option.id ?? option.key ?? option.label ?? option.name ?? ''
      ).trim();
      label = String(option.label ?? option.name ?? value).trim();
    } else {
      value = String(option ?? '').trim();
      label = value;
    }
    if (!value || seenValues.has(value)) return;
    seenValues.add(value);
    options.push({ value, label: label || value });
  });
  return options;
}

function normalizeInlineWebFormSchema(rawSchema) {
  const schema = rawSchema && typeof rawSchema === 'object' ? rawSchema : {};
  const title = String(schema.title || '').trim();
  const description = String(schema.description || '').trim();
  const submitLabel = String(schema.submit_label || '').trim() || 'Submit';
  const successMessage = String(schema.success_message || '').trim() || 'Submission received. Thank you.';
  const fieldsIn = Array.isArray(schema.fields) ? schema.fields : [];
  const seenIds = new Set();
  const fields = fieldsIn
    .map((field, index) => {
      if (!field || typeof field !== 'object') return null;
      let fieldId = String(field.id || `field_${index + 1}`).trim().toLowerCase();
      fieldId = fieldId.replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '') || `field_${index + 1}`;
      if (seenIds.has(fieldId)) return null;
      seenIds.add(fieldId);
      const rawType = String(field.type || '').trim().toLowerCase();
      const allowedTypes = new Set(['text', 'textarea', 'number', 'date', 'select', 'multi_select', 'boolean']);
      const type = allowedTypes.has(rawType) ? rawType : 'text';
      const label = String(field.label || fieldId).trim() || fieldId;
      const normalized = {
        id: fieldId,
        type,
        label,
        required: Boolean(field.required),
        placeholder: String(field.placeholder || '').trim()
      };
      if (type === 'select' || type === 'multi_select') {
        normalized.options = parseWebFormFieldOptions(field.options);
      }
      if (type === 'number') {
        const min = Number(field.min);
        const max = Number(field.max);
        const step = Number(field.step);
        if (Number.isFinite(min)) normalized.min = min;
        if (Number.isFinite(max)) normalized.max = max;
        if (Number.isFinite(step) && step > 0) normalized.step = step;
      }
      if (field.default !== undefined) normalized.default = field.default;
      return normalized;
    })
    .filter(Boolean);
  return {
    title,
    description,
    submitLabel,
    successMessage,
    fields
  };
}

function buildInlineWebFormDefaults(schema) {
  const defaults = {};
  const fields = Array.isArray(schema?.fields) ? schema.fields : [];
  fields.forEach((field) => {
    if (field.type === 'multi_select') {
      const rawDefault = field.default;
      const list = Array.isArray(rawDefault)
        ? rawDefault.map((item) => String(item || '').trim()).filter(Boolean)
        : (String(rawDefault || '').trim() ? [String(rawDefault).trim()] : []);
      const allowed = Array.isArray(field.options) ? new Set(field.options.map((opt) => opt.value)) : null;
      defaults[field.id] = allowed ? list.filter((item) => allowed.has(item)) : list;
      return;
    }
    if (field.type === 'boolean') {
      defaults[field.id] = Boolean(field.default);
      return;
    }
    if (field.type === 'number') {
      const raw = Number(field.default);
      defaults[field.id] = Number.isFinite(raw) ? raw : '';
      return;
    }
    if (field.type === 'select') {
      const options = Array.isArray(field.options) ? field.options : [];
      const defaultValue = String(field.default ?? '').trim();
      const fallback = options[0]?.value || '';
      defaults[field.id] = options.some((option) => option.value === defaultValue)
        ? defaultValue
        : fallback;
      return;
    }
    defaults[field.id] = String(field.default ?? '');
  });
  return defaults;
}

function normalizeInlineWebFormFieldValue(field, rawValue) {
  if (!field || typeof field !== 'object') return rawValue;
  if (field.type === 'boolean') {
    return Boolean(rawValue);
  }
  if (field.type === 'multi_select') {
    const rawList = Array.isArray(rawValue) ? rawValue : [];
    const cleaned = rawList.map((item) => String(item || '').trim()).filter(Boolean);
    const options = Array.isArray(field.options) ? new Set(field.options.map((opt) => opt.value)) : null;
    if (!options) return cleaned;
    return cleaned.filter((item) => options.has(item));
  }
  if (field.type === 'number') {
    if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
    const value = Number(rawValue);
    if (!Number.isFinite(value)) return '';
    return value;
  }
  if (field.type === 'select') {
    const value = String(rawValue ?? '').trim();
    const options = Array.isArray(field.options) ? field.options.map((opt) => opt.value) : [];
    if (!options.includes(value)) return options[0] || '';
    return value;
  }
  return String(rawValue ?? '');
}

function isInlineWebFormFieldEmpty(field, value) {
  if (!field?.required) return false;
  if (field.type === 'boolean') return value !== true;
  if (field.type === 'multi_select') return !Array.isArray(value) || value.length === 0;
  if (field.type === 'number') {
    return value === '' || value === null || value === undefined || !Number.isFinite(Number(value));
  }
  return String(value ?? '').trim() === '';
}

function buildWidgetRowId() {
  return `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function buildWidgetFieldDefault(field) {
  if (!field || typeof field !== 'object') return '';
  if (field.type === 'boolean') return Boolean(field.default);
  if (field.type === 'label') return String(field.default ?? '');
  if (field.type === 'multi_select' || field.type === 'tags') {
    return Array.isArray(field.default) ? [...field.default] : [];
  }
  if (field.type === 'select' || field.type === 'radio') {
    const options = Array.isArray(field.options) ? field.options : [];
    const defaultValue = String(field.default ?? '').trim();
    const fallback = options[0]?.value || '';
    return options.some((item) => item.value === defaultValue) ? defaultValue : fallback;
  }
  if (field.type === 'integer' || field.type === 'decimal' || field.type === 'slider') {
    const { min, max } = getWidgetNumericBounds(field);
    const defaultValue = normalizeWidgetNumericValue(field.default, null);
    let value = Number.isFinite(defaultValue)
      ? defaultValue
      : (Number.isFinite(min) ? min : 0);
    if (Number.isFinite(min) && value < min) value = min;
    if (Number.isFinite(max) && value > max) value = max;
    return field.type === 'integer' ? Math.round(value) : value;
  }
  if (field.type === 'table_editor') {
    const rows = Array.isArray(field.default) ? field.default : [];
    return rows
      .map((row) => normalizeWidgetTableRow(field, row))
      .filter(Boolean);
  }
  return String(field.default ?? '');
}

function normalizeWidgetTableRow(field, rawRow) {
  if (!field || !Array.isArray(field.columns) || !rawRow || typeof rawRow !== 'object') return null;
  const output = {};
  output._row_id = String(rawRow._row_id || '').trim() || buildWidgetRowId();
  let hasValue = false;
  field.columns.forEach((column) => {
    const value = normalizeWidgetFieldValue(column, rawRow[column.id]);
    output[column.id] = value;
    if (
      value !== '' &&
      value !== null &&
      value !== undefined &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      hasValue = true;
    }
  });
  return hasValue ? output : null;
}

function createWidgetEmptyTableRow(field) {
  const row = { _row_id: buildWidgetRowId() };
  const columns = Array.isArray(field?.columns) ? field.columns : [];
  columns.forEach((column) => {
    row[column.id] = buildWidgetFieldDefault(column);
  });
  return row;
}

function buildWidgetDefaults(widgetDefinition) {
  const defaults = {};
  if (!widgetDefinition?.fields) return defaults;
  widgetDefinition.fields.forEach((field) => {
    defaults[field.id] = buildWidgetFieldDefault(field);
  });
  return defaults;
}

function normalizeWidgetFieldValue(field, rawValue) {
  if (!field || typeof field !== 'object') return rawValue;
  if (field.type === 'boolean') return Boolean(rawValue);
  if (field.type === 'label') return String(rawValue ?? field.default ?? '').slice(0, 4000);
  if (field.type === 'multi_select') {
    const values = Array.isArray(rawValue)
      ? rawValue
      : String(rawValue ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    const options = Array.isArray(field.options) ? new Set(field.options.map((item) => item.value)) : null;
    const cleaned = [];
    const seen = new Set();
    values.forEach((item) => {
      const value = String(item || '').trim();
      if (!value || seen.has(value)) return;
      if (options && !options.has(value)) return;
      seen.add(value);
      cleaned.push(value);
    });
    return cleaned;
  }
  if (field.type === 'tags') {
    const values = Array.isArray(rawValue)
      ? rawValue
      : String(rawValue ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    const cleaned = [];
    const seen = new Set();
    values.forEach((item) => {
      const value = String(item || '').trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      cleaned.push(value);
    });
    return cleaned.slice(0, 30);
  }
  if (field.type === 'select' || field.type === 'radio') {
    const value = String(rawValue ?? '').trim();
    const options = Array.isArray(field.options) ? field.options.map((item) => item.value) : [];
    if (!options.length) return '';
    return options.includes(value) ? value : options[0];
  }
  if (field.type === 'slider' || field.type === 'integer' || field.type === 'decimal') {
    const { min, max } = getWidgetNumericBounds(field);
    const fallbackRaw = normalizeWidgetNumericValue(field.default, null);
    const fallback = Number.isFinite(fallbackRaw)
      ? fallbackRaw
      : (Number.isFinite(min) ? min : 0);
    let value = normalizeWidgetNumericValue(rawValue, fallback);
    if (Number.isFinite(min) && value < min) value = min;
    if (Number.isFinite(max) && value > max) value = max;
    return field.type === 'integer' ? Math.round(value) : Number(value);
  }
  if (field.type === 'table_editor') {
    if (!Array.isArray(rawValue)) return [];
    return rawValue
      .map((row) => normalizeWidgetTableRow(field, row))
      .filter(Boolean)
      .slice(0, 400);
  }
  if (field.type === 'date' || field.type === 'time' || field.type === 'datetime') {
    return String(rawValue ?? '').trim().slice(0, 64);
  }
  if (field.type === 'textarea') return String(rawValue ?? '').slice(0, 4000);
  return String(rawValue ?? '').slice(0, 1000);
}

function renderTableArtifact(content) {
  const parsed = safeJsonParse(content);
  let columns = [];
  let rows = [];

  if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.columns) && Array.isArray(parsed.rows)) {
      columns = parsed.columns;
      if (Array.isArray(parsed.rows[0]) || parsed.rows.length === 0) {
        rows = parsed.rows;
      } else {
        rows = parsed.rows.map((r) => columns.map((c) => r?.[c] ?? ''));
      }
    } else if (Array.isArray(parsed.headers) && Array.isArray(parsed.rows)) {
      columns = parsed.headers;
      if (Array.isArray(parsed.rows[0]) || parsed.rows.length === 0) {
        rows = parsed.rows;
      } else {
        rows = parsed.rows.map((r) => columns.map((c) => r?.[c] ?? ''));
      }
    } else if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
      columns = Object.keys(parsed[0]);
      rows = parsed.map((r) => columns.map((c) => r[c]));
    }
  }

  if (!columns.length || !rows.length) {
    return <pre className="artifact-fallback">{content}</pre>;
  }

  return (
    <div className="artifact-table-wrap">
      <table className="artifact-table-el">
        <thead>
          <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx}>
              {r.map((cell, cellIdx) => <td key={`${idx}-${cellIdx}`}>{String(cell)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderChartArtifact(content) {
  const parsed = safeJsonParse(content);
  const data = Array.isArray(parsed?.data) ? parsed.data : [];
  if (!data.length) return <pre className="artifact-fallback">{content}</pre>;
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  const unit = parsed?.unit ? ` ${parsed.unit}` : '';

  return (
    <div className="artifact-chart">
      {data.map((d, idx) => {
        const value = Number(d.value) || 0;
        const width = `${Math.max(4, (value / max) * 100)}%`;
        return (
          <div key={`${d.label}-${idx}`} className="chart-row">
            <span className="chart-label">{d.label}</span>
            <div className="chart-bar-track">
              <div className="chart-bar" style={{ width }} />
            </div>
            <span className="chart-value">{value}{unit}</span>
          </div>
        );
      })}
    </div>
  );
}

function renderImageArtifact(content, title, documents = [], accessToken = '') {
  const parsed = safeJsonParse(content);
  let url = '';
  if (typeof parsed?.url === 'string') {
    url = parsed.url.trim();
  }
  if (!url && typeof parsed?.download_path === 'string') {
    url = buildDownloadUrlFromPath(parsed.download_path, accessToken);
  }
  if (!url) {
    const documentId = Number(parsed?.document_id || parsed?.doc_id || parsed?.documentId || 0);
    if (Number.isFinite(documentId) && documentId > 0) {
      const doc = Array.isArray(documents)
        ? documents.find((item) => Number(item?.id) === Number(documentId))
        : null;
      if (doc) {
        url = buildDocumentDownloadUrl(doc, accessToken);
      }
    }
  }
  if (!url) {
    url = content.trim();
  }
  const caption = typeof parsed?.caption === 'string' ? parsed.caption : title;
  if (!url) return <pre className="artifact-fallback">{content}</pre>;
  return (
    <figure className="artifact-image-wrap">
      <img src={url} alt={caption || 'artifact'} className="artifact-image" />
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

function renderStaticArtifactBody(artifact, documents = [], accessToken = '') {
  const type = normalizeArtifactType(artifact.artifact_type);
  if (type === 'postit') return <div className="artifact-postit-body">{artifact.content}</div>;
  if (type === 'table') return renderTableArtifact(artifact.content);
  if (type === 'chart') return renderChartArtifact(artifact.content);
  if (type === 'image') return renderImageArtifact(artifact.content, artifact.title, documents, accessToken);
  return <pre className="artifact-fallback">{artifact.content}</pre>;
}

function AgentsHome({ currentUser, onLogout }) {
  const { t } = useI18n();
  const [agents, setAgents] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [account, setAccount] = useState(currentUser || null);
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');
  const [systemInstructionsDraft, setSystemInstructionsDraft] = useState(emptyAgentSystemInstructions());
  const [showAdvancedCreate, setShowAdvancedCreate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showLibraryOverlay, setShowLibraryOverlay] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      const [agentsResponse, meResponse, templatesResponse] = await Promise.all([
        axios.get(`${API_URL}/agents`),
        axios.get(`${API_URL}/auth/me`),
        axios.get(`${API_URL}/templates/public`)
      ]);
      setAgents(agentsResponse?.data?.data || []);
      setAccount(meResponse?.data?.data || currentUser || null);
      setPublicTemplates(templatesResponse?.data?.data || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    setAccount(currentUser || null);
  }, [currentUser]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !objective.trim()) return;
    try {
      setLoading(true);
      const systemInstructions = systemInstructionsDraftToPayload(systemInstructionsDraft);
      const response = await axios.post(`${API_URL}/agents`, {
        name: name.trim(),
        objective: objective.trim(),
        system_instructions: systemInstructions,
        source_template_id: selectedTemplate?.id || null
      });
      const createdAgentId = Number(response?.data?.data?.id || 0);
      if (Number.isFinite(createdAgentId) && createdAgentId > 0) {
        navigate(`/agents/${createdAgentId}`);
        return;
      }
      await loadAgents();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const handleDelete = async (agentId) => {
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/agents/${agentId}`);
      await loadAgents();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const createAgentFromTemplate = useCallback(async (templateId) => {
    const numericTemplateId = Number(templateId || 0);
    if (!Number.isFinite(numericTemplateId) || numericTemplateId <= 0) return;
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/templates/${numericTemplateId}/create-agent`, {});
      const createdAgentId = Number(response?.data?.data?.id || 0);
      if (Number.isFinite(createdAgentId) && createdAgentId > 0) {
        navigate(`/agents/${createdAgentId}`);
        return;
      }
      await loadAgents();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  }, [loadAgents, navigate]);

  const prefillFromTemplate = useCallback((template) => {
    if (!template || typeof template !== 'object') return;
    const payload = (template.template && typeof template.template === 'object') ? template.template : {};
    const nextName = String(payload.agent_name || template.name || '').trim();
    const nextObjective = String(payload.objective || '').trim();
    setName(nextName);
    setObjective(nextObjective);
    setSystemInstructionsDraft(instructionsPayloadToDraft(payload.system_instructions));
    setShowAdvancedCreate(Boolean(payload.system_instructions));
    setSelectedTemplate({
      id: Number(template.id || 0),
      name: String(template.name || nextName || '').trim()
    });
    setShowLibraryOverlay(false);
  }, []);

  const clearSelectedTemplate = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  const filteredTemplates = useMemo(() => {
    const query = String(libraryQuery || '').trim().toLowerCase();
    const templates = Array.isArray(publicTemplates) ? publicTemplates : [];
    if (!query) return templates;
    return templates.filter((template) => {
      const payload = (template?.template && typeof template.template === 'object') ? template.template : {};
      const haystack = [
        template?.name,
        template?.description,
        template?.owner_username,
        payload?.objective,
        payload?.agent_name,
      ]
        .map((item) => String(item || '').toLowerCase())
        .join(' ');
      return haystack.includes(query);
    });
  }, [libraryQuery, publicTemplates]);

  const viewer = account || currentUser || null;
  const creditsExhausted = Boolean(viewer?.credits_exhausted);
  const createDisabled = loading || !name.trim() || !objective.trim();

  const resolveTemplateShareUrl = useCallback((template) => {
    const sharePath = String(template?.share_path || '').trim();
    if (sharePath && typeof window !== 'undefined') {
      return `${window.location.origin}${sharePath}`;
    }
    return '';
  }, []);

  const copyTemplateShareLink = useCallback(async (template) => {
    const url = resolveTemplateShareUrl(template);
    if (!url || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard can be blocked by browser context.
    }
  }, [resolveTemplateShareUrl]);

  return (
    <div className="window">
      <div className="topbar">
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('common.appName')}</div>
        <div className="topbar-actions topbar-actions--home">
          {viewer?.is_admin ? (
            <>
              <button type="button" className="home-nav-btn" onClick={() => navigate('/admin')} aria-label={t('common.admin')}>
                <span className="btn-icon" aria-hidden="true">⌘</span>
                <span className="btn-label">{t('common.admin')}</span>
              </button>
              <button type="button" className="home-nav-btn" onClick={() => navigate('/captain')} aria-label={t('common.captain')}>
                <span className="btn-icon" aria-hidden="true">✦</span>
                <span className="btn-label">{t('common.captain')}</span>
              </button>
            </>
          ) : null}
          <span className="meta">{viewer?.username || ''}</span>
          <span className={`top-credit ${creditsExhausted ? 'is-low' : ''}`} title={userCreditsLabel(viewer, t)}>
            {compactCreditsLabel(viewer, t)}
          </span>
          <LanguagePicker className="locale-switch--top" />
          <button type="button" className="home-nav-btn" onClick={onLogout} aria-label={t('common.logout')}>
            <span className="btn-icon" aria-hidden="true">⏻</span>
            <span className="btn-label">{t('common.logout')}</span>
          </button>
        </div>
      </div>
      <div className="page">
        {creditsExhausted ? (
          <section className="card credit-warning">
            <p className="error">
              {t('credits.homeExhausted')}
            </p>
          </section>
        ) : null}
        <header className="hero">
          <h1 className="visually-hidden">{t('common.appName')}</h1>
          <p className="hero-kicker">{t('home.heroKicker')}</p>
          <div className="hero-brand">
            <div className="hero-logo" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 3.8 19.2 8v8L12 20.2 4.8 16V8z" />
                <path d="M12 3.8v8.2m0 0L4.8 8m7.2 4 7.2-4" />
              </svg>
            </div>
            <p className="hero-lead">
              {t('home.heroLead')}
            </p>
          </div>
        </header>

        <section className="card">
          <div className="card-head">
            <h2>{t('home.createAgent')}</h2>
            <button
              type="button"
              className="agents-action-btn agents-open-btn"
              onClick={() => {
                setShowLibraryOverlay(true);
                setLibraryQuery('');
              }}
              disabled={loading}
              aria-label={t('home.browseTemplateLibrary')}
              title={t('home.browseTemplateLibrary')}
            >
              <span className="btn-icon" aria-hidden="true">⌕</span>
              <span className="btn-label">{t('home.browseTemplateLibrary')}</span>
            </button>
          </div>
          <form onSubmit={handleCreate} className="agent-form">
            {selectedTemplate ? (
              <div className="template-selected-banner">
                <span className="meta">
                  {t('home.usingTemplate', { name: selectedTemplate.name || `#${selectedTemplate.id}` })}
                </span>
                <button
                  type="button"
                  className="row-icon-btn row-icon-btn--danger"
                  onClick={clearSelectedTemplate}
                  disabled={loading}
                  aria-label={t('home.clearTemplateSelection')}
                  title={t('home.clearTemplateSelection')}
                >
                  x
                </button>
              </div>
            ) : null}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('home.agentNamePlaceholder')}
              disabled={loading}
            />
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder={t('home.objectivePlaceholder')}
              disabled={loading}
              rows={3}
            />
            <button
              type="button"
              onClick={() => setShowAdvancedCreate((prev) => !prev)}
              disabled={loading}
            >
              {showAdvancedCreate ? t('home.hideAdvancedInstructions') : t('home.showAdvancedInstructions')}
            </button>
            {showAdvancedCreate ? (
              <AgentSystemInstructionsFields
                draft={systemInstructionsDraft}
                setDraft={setSystemInstructionsDraft}
                disabled={loading}
                t={t}
                className="advanced-create-grid"
              />
            ) : null}
            <button type="submit" disabled={createDisabled}>
              {loading ? t('common.saving') : t('common.create')}
            </button>
          </form>
        </section>

        <section className="card agents-existing-card">
          <div className="card-head">
            <h2>{t('home.existingAgents')}</h2>
            <button
              className="agents-action-btn agents-refresh-btn"
              onClick={loadAgents}
              disabled={loading}
              aria-label={t('home.refreshAgents')}
              title={t('home.refreshAgents')}
            >
              <span className="btn-icon" aria-hidden="true">↻</span>
              <span className="btn-label">{t('common.refresh')}</span>
            </button>
          </div>
          {agents.length === 0 ? (
            <p className="muted">{t('home.noAgents')}</p>
          ) : (
            <ul className="agent-list agent-list--home">
              {agents.map((agent) => (
                <li key={agent.id} className="agent-row agent-row--home">
                  <div>
                    <strong>{agent.name}</strong>
                    <p className="objective-preview">{agent.objective}</p>
                    <span className="meta">{t('common.lastRound', { value: formatDate(agent.last_round_at) })}</span>
                  </div>
                  <div className="row-actions row-actions--home">
                    <button
                      className="agents-action-btn agents-open-btn"
                      onClick={() => navigate(`/agents/${agent.id}`)}
                      aria-label={t('home.openAgent', { name: agent.name })}
                      title={t('common.open')}
                    >
                      <span className="btn-icon" aria-hidden="true">↗</span>
                      <span className="btn-label">{t('common.open')}</span>
                    </button>
                    <button
                      className="agents-action-btn danger-btn agents-delete-btn"
                      onClick={() => handleDelete(agent.id)}
                      aria-label={t('home.deleteAgent', { name: agent.name })}
                      title={t('common.delete')}
                    >
                      <span className="btn-icon" aria-hidden="true">×</span>
                      <span className="btn-label">{t('common.delete')}</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="error">{error}</p>}
        </section>
      </div>

      {showLibraryOverlay ? (
        <div
          className="template-library-overlay"
          onClick={() => setShowLibraryOverlay(false)}
          role="presentation"
        >
          <section
            className="template-library-modal card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('home.libraryOverlayTitle')}
          >
            <div className="card-head">
              <h2>{t('home.libraryOverlayTitle')}</h2>
              <button
                type="button"
                className="agents-action-btn agents-delete-btn"
                onClick={() => setShowLibraryOverlay(false)}
                aria-label={t('home.libraryClose')}
                title={t('home.libraryClose')}
              >
                <span className="btn-icon" aria-hidden="true">×</span>
                <span className="btn-label">{t('home.libraryClose')}</span>
              </button>
            </div>
            <input
              className="template-library-search"
              type="text"
              value={libraryQuery}
              onChange={(e) => setLibraryQuery(e.target.value)}
              placeholder={t('home.librarySearchPlaceholder')}
              disabled={loading}
            />
            {filteredTemplates.length === 0 ? (
              <p className="muted">{t('home.libraryNoMatch')}</p>
            ) : (
              <ul className="agent-list template-library-list">
                {filteredTemplates.map((template) => {
                  const payload = (template.template && typeof template.template === 'object') ? template.template : {};
                  const objectivePreview = String(payload.objective || '').trim();
                  const shareUrl = resolveTemplateShareUrl(template);
                  return (
                    <li key={`tpl-modal-${template.id}`} className="agent-row template-library-row">
                      <div className="template-library-main">
                        <strong>{template.name}</strong>
                        <span className="meta">{t('home.templateBy', { owner: template.owner_username || '-' })}</span>
                        {template.description ? <p className="objective-preview">{template.description}</p> : null}
                        {objectivePreview ? <p className="objective-preview">{objectivePreview}</p> : null}
                        <span className="meta">{t('common.lastLogin', { value: formatDate(template.updated_at || template.created_at) })}</span>
                        {shareUrl ? (
                          <div className="template-share-line">
                            <span className="meta">{shareUrl}</span>
                            <button type="button" onClick={() => copyTemplateShareLink(template)} disabled={loading}>
                              {t('home.libraryCopyLink')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div className="row-actions template-library-actions">
                        <button
                          type="button"
                          className="agents-action-btn agents-open-btn"
                          onClick={() => createAgentFromTemplate(template.id)}
                          disabled={loading}
                          aria-label={t('home.libraryCreateNow', { name: template.name })}
                          title={t('home.libraryCreateNow', { name: template.name })}
                        >
                          <span className="btn-icon" aria-hidden="true">↗</span>
                          <span className="btn-label">{t('home.libraryCreateCta')}</span>
                        </button>
                        <button
                          type="button"
                          className="agents-action-btn"
                          onClick={() => prefillFromTemplate(template)}
                          disabled={loading}
                          aria-label={t('home.libraryPrefillNow', { name: template.name })}
                          title={t('home.libraryPrefillNow', { name: template.name })}
                        >
                          <span className="btn-icon" aria-hidden="true">↘</span>
                          <span className="btn-label">{t('home.libraryPrefillCta')}</span>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

function CaptainConsole({ currentUser, accessToken, onLogout }) {
  const { t } = useI18n();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamState, setStreamState] = useState('connecting');
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  ));
  const [mobileTab, setMobileTab] = useState('build');
  const [manualForm, setManualForm] = useState({ agent_id: '', tool_name: '', specification: '' });
  const [manualWebAppForm, setManualWebAppForm] = useState({
    agent_id: '',
    app_name: '',
    specification: '',
    link_count: '1',
    create_linked_tool: true
  });

  const loadOverview = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API_URL}/captain/overview`);
      setOverview(response.data.data || null);
      if (!silent) setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let fallbackTimer = null;
    let stream = null;
    let cancelled = false;

    const startFallbackPolling = () => {
      if (fallbackTimer) return;
      fallbackTimer = setInterval(() => loadOverview(true), 2500);
    };

    loadOverview(true);
    try {
      stream = new EventSource(buildSseUrl('/captain/stream', accessToken));
      stream.addEventListener('overview', (event) => {
        if (cancelled) return;
        try {
          const payload = JSON.parse(event.data);
          setOverview(payload || null);
          setStreamState('live');
          setError('');
        } catch {
          setStreamState('fallback');
        }
      });
      stream.onopen = () => {
        if (cancelled) return;
        setStreamState('live');
      };
      stream.onerror = () => {
        if (cancelled) return;
        setStreamState('fallback');
        startFallbackPolling();
      };
    } catch {
      setStreamState('fallback');
      startFallbackPolling();
    }

    return () => {
      cancelled = true;
      if (stream) stream.close();
      if (fallbackTimer) clearInterval(fallbackTimer);
    };
  }, [loadOverview, accessToken]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const submitManualBuild = async (e) => {
    e.preventDefault();
    const agentId = Number(manualForm.agent_id);
    if (!Number.isFinite(agentId) || agentId <= 0) return;
    if (!manualForm.tool_name.trim() || !manualForm.specification.trim()) return;
    try {
      setLoading(true);
      await axios.post(`${API_URL}/agents/${agentId}/captain/build-tool`, {
        tool_name: manualForm.tool_name.trim(),
        specification: manualForm.specification.trim()
      });
      setManualForm({ agent_id: '', tool_name: '', specification: '' });
      await loadOverview(true);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const submitManualWebAppBuild = async (e) => {
    e.preventDefault();
    const agentId = Number(manualWebAppForm.agent_id);
    const linkCount = Number(manualWebAppForm.link_count || 1);
    if (!Number.isFinite(agentId) || agentId <= 0) return;
    if (!manualWebAppForm.app_name.trim() || !manualWebAppForm.specification.trim()) return;
    try {
      setLoading(true);
      await axios.post(`${API_URL}/agents/${agentId}/captain/build-webapp`, {
        app_name: manualWebAppForm.app_name.trim(),
        specification: manualWebAppForm.specification.trim(),
        link_count: Number.isFinite(linkCount) ? Math.max(1, Math.min(20, linkCount)) : 1,
        create_linked_tool: Boolean(manualWebAppForm.create_linked_tool)
      });
      setManualWebAppForm({
        agent_id: '',
        app_name: '',
        specification: '',
        link_count: '1',
        create_linked_tool: true
      });
      await loadOverview(true);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const stats = overview?.stats || {};
  const tools = overview?.tools || [];
  const jobs = overview?.jobs || [];
  const webApps = overview?.web_apps || [];
  const webAppJobs = overview?.web_app_jobs || [];

  return (
    <div className="window">
      <div className={`topbar ${isMobileViewport ? 'topbar--mobile-compact' : ''}`}>
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('captain.title')}</div>
        <div className={`topbar-actions ${isMobileViewport ? 'topbar-actions--mobile' : ''}`}>
          {!isMobileViewport ? <span className="meta">{currentUser?.username || ''}</span> : null}
          <span className="top-credit" title={userCreditsLabel(currentUser, t)}>
            {compactCreditsLabel(currentUser, t)}
          </span>
          <LanguagePicker className="locale-switch--top" />
          <Link
            to="/"
            className={`top-link ${isMobileViewport ? 'top-link--mobile' : ''}`}
            aria-label={t('common.back')}
            title={t('common.back')}
          >
            {isMobileViewport ? '←' : t('common.back')}
          </Link>
          <button
            type="button"
            className={isMobileViewport ? 'top-btn--mobile' : ''}
            onClick={onLogout}
            aria-label={t('common.logout')}
            title={t('common.logout')}
          >
            {isMobileViewport ? '⏻' : t('common.logout')}
          </button>
        </div>
      </div>
      <div className="page">
        <header className="hero">
          <h1 className="visually-hidden">{t('captain.title')}</h1>
          <p className="hero-kicker">{t('captain.heroKicker')}</p>
          <p className="hero-lead">{t('captain.heroLead')}</p>
          <span className={`captain-stream-state captain-stream-state--${streamState}`}>
            {streamState === 'live' ? t('captain.liveUpdates') : (streamState === 'connecting' ? t('captain.connecting') : t('captain.fallbackSync'))}
          </span>
          <div className="captain-stats">
            <span className="captain-stat">{t('captain.stats.tools', { value: stats.tools_total || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.active', { value: stats.tools_active || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.toolJobsRunning', { value: stats.jobs_running || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.toolJobsQueued', { value: stats.jobs_queued || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.webApps', { value: stats.web_apps_total || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.webJobsRunning', { value: stats.web_jobs_running || 0 })}</span>
            <span className="captain-stat">{t('captain.stats.webJobsQueued', { value: stats.web_jobs_queued || 0 })}</span>
          </div>
        </header>
        {isMobileViewport ? (
          <div className="mobile-section-tabs">
            <button
              type="button"
              className={`mobile-section-tab ${mobileTab === 'build' ? 'active' : ''}`}
              onClick={() => setMobileTab('build')}
            >
              {t('tabs.build')}
            </button>
            <button
              type="button"
              className={`mobile-section-tab ${mobileTab === 'catalog' ? 'active' : ''}`}
              onClick={() => setMobileTab('catalog')}
            >
              {t('tabs.catalog')}
            </button>
            <button
              type="button"
              className={`mobile-section-tab ${mobileTab === 'jobs' ? 'active' : ''}`}
              onClick={() => setMobileTab('jobs')}
            >
              {t('tabs.jobs')}
            </button>
          </div>
        ) : null}

        {(!isMobileViewport || mobileTab === 'build') ? (
        <section className="card captain-section captain-section--build">
          <div className="card-head">
            <h2>{t('captain.requestBuild')}</h2>
            <button
              type="button"
              className="agents-action-btn captain-sync-btn"
              onClick={() => loadOverview()}
              disabled={loading}
              aria-label={t('common.syncNow')}
              title={t('common.syncNow')}
            >
              <span className="btn-icon" aria-hidden="true">↻</span>
              <span className="btn-label">{t('common.syncNow')}</span>
            </button>
          </div>
          <form onSubmit={submitManualBuild} className="agent-form">
            <input
              type="number"
              min="1"
              placeholder={t('captain.requesterAgentId')}
              value={manualForm.agent_id}
              onChange={(e) => setManualForm((prev) => ({ ...prev, agent_id: e.target.value }))}
              disabled={loading}
            />
            <input
              type="text"
              placeholder={t('captain.toolName')}
              value={manualForm.tool_name}
              onChange={(e) => setManualForm((prev) => ({ ...prev, tool_name: e.target.value }))}
              disabled={loading}
            />
            <textarea
              rows={3}
              placeholder={t('captain.toolSpecification')}
              value={manualForm.specification}
              onChange={(e) => setManualForm((prev) => ({ ...prev, specification: e.target.value }))}
              disabled={loading}
            />
            <button
              type="submit"
              disabled={
                loading ||
                !manualForm.agent_id.trim() ||
                !manualForm.tool_name.trim() ||
                !manualForm.specification.trim()
              }
            >
              {t('captain.queueBuild')}
            </button>
          </form>
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'build') ? (
        <section className="card captain-section captain-section--build">
          <div className="card-head">
            <h2>{t('captain.requestWebApp')}</h2>
          </div>
          <form onSubmit={submitManualWebAppBuild} className="agent-form">
            <input
              type="number"
              min="1"
              placeholder={t('captain.requesterAgentId')}
              value={manualWebAppForm.agent_id}
              onChange={(e) => setManualWebAppForm((prev) => ({ ...prev, agent_id: e.target.value }))}
              disabled={loading}
            />
            <input
              type="text"
              placeholder={t('captain.webAppName')}
              value={manualWebAppForm.app_name}
              onChange={(e) => setManualWebAppForm((prev) => ({ ...prev, app_name: e.target.value }))}
              disabled={loading}
            />
            <input
              type="number"
              min="1"
              max="20"
              placeholder={t('captain.linkCount')}
              value={manualWebAppForm.link_count}
              onChange={(e) => setManualWebAppForm((prev) => ({ ...prev, link_count: e.target.value }))}
              disabled={loading}
            />
            <label className="check">
              <input
                type="checkbox"
                checked={Boolean(manualWebAppForm.create_linked_tool)}
                onChange={(e) => setManualWebAppForm((prev) => ({ ...prev, create_linked_tool: e.target.checked }))}
                disabled={loading}
              />
              <span>{t('captain.createLinkedCapTool')}</span>
            </label>
            <textarea
              rows={3}
              placeholder={t('captain.webAppSpecification')}
              value={manualWebAppForm.specification}
              onChange={(e) => setManualWebAppForm((prev) => ({ ...prev, specification: e.target.value }))}
              disabled={loading}
            />
            <button
              type="submit"
              disabled={
                loading ||
                !manualWebAppForm.agent_id.trim() ||
                !manualWebAppForm.app_name.trim() ||
                !manualWebAppForm.specification.trim()
              }
            >
              {t('captain.queueWebApp')}
            </button>
          </form>
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'catalog') ? (
        <section className="card captain-section captain-section--catalog">
          <h2>{t('captain.managedTools')}</h2>
          {tools.length === 0 ? (
            <p className="muted">{t('captain.noManagedTools')}</p>
          ) : (
            <ul className="agent-list">
              {tools.map((tool) => (
                <li key={tool.id} className="agent-row">
                  <div>
                    <strong>{tool.name}</strong>
                    <span className="meta">{t('captain.statusMeta', { id: tool.tool_code, status: tool.status })}</span>
                    <p className="objective-preview">{tool.description}</p>
                    <span className="meta">
                      {t('captain.usageMeta', { usage: tool.usage_count || 0, date: formatDate(tool.last_used_at) })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'catalog') ? (
        <section className="card captain-section captain-section--catalog">
          <h2>{t('captain.managedWebApps')}</h2>
          {webApps.length === 0 ? (
            <p className="muted">{t('captain.noManagedWebApps')}</p>
          ) : (
            <ul className="agent-list">
              {webApps.map((app) => (
                <li key={app.id} className="agent-row">
                  <div>
                    <strong>{app.name}</strong>
                    <span className="meta">{t('captain.statusMeta', { id: app.app_code, status: app.status })}</span>
                    <p className="objective-preview">{app.description}</p>
                    <span className="meta">
                      {t('captain.ownerUsage', {
                        agent: app.requester_agent_id || '-',
                        usage: app.usage_count || 0,
                        date: formatDate(app.last_used_at)
                      })}
                    </span>
                    {app.usage_instructions ? <span className="meta">{t('captain.howToUse', { value: app.usage_instructions })}</span> : null}
                    <details className="captain-inner-details">
                      <summary>{t('captain.linksSummary', { count: (app.links || []).length })}</summary>
                      <ul className="compact-list">
                        {(app.links || []).map((link) => {
                          const fullUrl = buildWebFormUrl(link.link_code);
                          return (
                            <li key={link.id} className="resource-row">
                              <div className="resource-main">
                                <span className="resource-token resource-token--native">WEB</span>
                                <span className="resource-line" title={fullUrl}>
                                  <strong>{link.link_code}</strong>
                                  <span className="resource-sub">{fullUrl}</span>
                                </span>
                              </div>
                              <a href={fullUrl} target="_blank" rel="noreferrer">
                                <button type="button" className="agents-action-btn captain-open-link-btn">
                                  <span className="btn-icon" aria-hidden="true">↗</span>
                                  <span className="btn-label">{t('common.open')}</span>
                                </button>
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'jobs') ? (
        <section className="card captain-section captain-section--jobs">
          <h2>{t('captain.buildJobs')}</h2>
          {jobs.length === 0 ? (
            <p className="muted">{t('captain.noJobs')}</p>
          ) : (
            <ul className="agent-list captain-jobs-list">
              {jobs.map((job) => (
                <li key={job.id} className="agent-row captain-job-card">
                  <div>
                    <strong>{job.requested_name}</strong>
                    <span className={`meta captain-job-status captain-job-status--${job.status}`}>{job.status}</span>
                    <p className="objective-preview">{job.specification}</p>
                    <span className="meta">
                      {t('captain.iterationsMeta', { iter: job.iteration_count || 0, agent: job.requester_agent_id || '-' })}
                    </span>
                    {job.error ? <span className="meta">{t('common.error', { value: job.error })}</span> : null}
                    {(job.logs || []).length ? <span className="meta">{t('captain.lastLog', { value: job.logs[job.logs.length - 1] })}</span> : null}
                    <details className="captain-job-details" open={job.status === 'running'}>
                      <summary>{t('captain.iterationTrace', { count: (job.iterations || []).length })}</summary>
                      <div className="captain-iterations">
                        {(job.iterations || []).map((iteration) => (
                          <article key={iteration.id} className="captain-iteration">
                            <div className="captain-iteration-head">
                              <strong>
                                {t('captain.iterPhase', { iter: iteration.attempt_index, phase: iteration.phase })}
                              </strong>
                              <span className="meta">
                                {formatDate(iteration.created_at)}
                                {iteration.model ? ` · ${iteration.model}` : ''}
                              </span>
                            </div>
                            {iteration.summary ? <p className="objective-preview">{iteration.summary}</p> : null}
                            {(iteration.failures || []).length > 0 ? (
                              <ul className="captain-failures">
                                {(iteration.failures || []).map((failure, idx) => (
                                  <li key={`${iteration.id}-f-${idx}`}>{failure}</li>
                                ))}
                              </ul>
                            ) : null}
                            {(iteration.tests || []).length > 0 ? (
                              <details className="captain-inner-details">
                                <summary>{t('captain.unitTests', { count: iteration.tests.length })}</summary>
                                <pre className="captain-code-block">{JSON.stringify(iteration.tests, null, 2)}</pre>
                              </details>
                            ) : null}
                            {iteration.python_code ? (
                              <details
                                className="captain-inner-details"
                                open={job.status === 'running' && iteration.id === job.latest_iteration?.id}
                              >
                                <summary>{t('captain.generatedScript')}</summary>
                                <pre className="captain-code-block">{iteration.python_code}</pre>
                              </details>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </details>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="error">{error}</p>}
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'jobs') ? (
        <section className="card captain-section captain-section--jobs">
          <h2>{t('captain.webAppJobs')}</h2>
          {webAppJobs.length === 0 ? (
            <p className="muted">{t('captain.noWebAppJobs')}</p>
          ) : (
            <ul className="agent-list captain-jobs-list">
              {webAppJobs.map((job) => (
                <li key={job.id} className="agent-row captain-job-card">
                  <div>
                    <strong>{job.requested_name}</strong>
                    <span className={`meta captain-job-status captain-job-status--${job.status}`}>{job.status}</span>
                    <p className="objective-preview">{job.specification}</p>
                    <span className="meta">
                      {t('captain.webIterationsMeta', {
                        iter: job.iteration_count || 0,
                        agent: job.requester_agent_id || '-',
                        links: job.link_count || 1,
                        linked: job.create_linked_tool === false ? t('common.no') : t('common.yes')
                      })}
                    </span>
                    {job.error ? <span className="meta">{t('common.error', { value: job.error })}</span> : null}
                    {(job.logs || []).length ? <span className="meta">{t('captain.lastLog', { value: job.logs[job.logs.length - 1] })}</span> : null}
                    <details className="captain-job-details" open={job.status === 'running'}>
                      <summary>{t('captain.iterationTrace', { count: (job.iterations || []).length })}</summary>
                      <div className="captain-iterations">
                        {(job.iterations || []).map((iteration) => (
                          <article key={iteration.id} className="captain-iteration">
                            <div className="captain-iteration-head">
                              <strong>
                                {t('captain.iterPhase', { iter: iteration.attempt_index, phase: iteration.phase })}
                              </strong>
                              <span className="meta">
                                {formatDate(iteration.created_at)}
                                {iteration.model ? ` · ${iteration.model}` : ''}
                              </span>
                            </div>
                            {iteration.summary ? <p className="objective-preview">{iteration.summary}</p> : null}
                            {(iteration.failures || []).length > 0 ? (
                              <ul className="captain-failures">
                                {(iteration.failures || []).map((failure, idx) => (
                                  <li key={`${iteration.id}-wf-f-${idx}`}>{failure}</li>
                                ))}
                              </ul>
                            ) : null}
                            {iteration.form_schema && Object.keys(iteration.form_schema || {}).length > 0 ? (
                              <details className="captain-inner-details">
                                <summary>{t('captain.formSchema')}</summary>
                                <pre className="captain-code-block">{JSON.stringify(iteration.form_schema, null, 2)}</pre>
                              </details>
                            ) : null}
                            {iteration.endpoint_python_code ? (
                              <details className="captain-inner-details">
                                <summary>{t('captain.endpointScript')}</summary>
                                <pre className="captain-code-block">{iteration.endpoint_python_code}</pre>
                              </details>
                            ) : null}
                            {iteration.linked_tool_python_code ? (
                              <details className="captain-inner-details">
                                <summary>{t('captain.linkedToolScript')}</summary>
                                <pre className="captain-code-block">{iteration.linked_tool_python_code}</pre>
                              </details>
                            ) : null}
                            {(iteration.tests || []).length > 0 ? (
                              <details className="captain-inner-details">
                                <summary>{t('captain.tests', { count: iteration.tests.length })}</summary>
                                <pre className="captain-code-block">{JSON.stringify(iteration.tests, null, 2)}</pre>
                              </details>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </details>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error ? <p className="error">{error}</p> : null}
        </section>
        ) : null}

        {error ? <p className="error">{error}</p> : null}
      </div>
    </div>
  );
}

function AgentWorkspace({ currentUser, accessToken, onLogout }) {
  const { t } = useI18n();
  const { agentId } = useParams();
  const [state, setState] = useState(null);
  const [objectiveDraft, setObjectiveDraft] = useState('');
  const [agentStreamState, setAgentStreamState] = useState('connecting');
  const [chatInput, setChatInput] = useState('');
  const [toolForm, setToolForm] = useState({
    name: '',
    mcp_server: '',
    auth_type: 'none',
    auth_header_name: '',
    auth_token: ''
  });
  const [docForm, setDocForm] = useState({ name: '', url: '' });
  const [questionDrafts, setQuestionDrafts] = useState({});
  const [widgetDrafts, setWidgetDrafts] = useState({});
  const [widgetSubmitting, setWidgetSubmitting] = useState({});
  const [inlineSubmitting, setInlineSubmitting] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [optimisticChatPending, setOptimisticChatPending] = useState(false);
  const [showLlmDebug, setShowLlmDebug] = useState(false);
  const [showToolCreate, setShowToolCreate] = useState(false);
  const [showDocActions, setShowDocActions] = useState(false);
  const [showDocLinkForm, setShowDocLinkForm] = useState(false);
  const [showTemplatePublish, setShowTemplatePublish] = useState(false);
  const [templatePublishForm, setTemplatePublishForm] = useState({
    visibility: 'public',
    name: '',
    description: ''
  });
  const [templatePublishResult, setTemplatePublishResult] = useState(null);
  const uploadInputRef = useRef(null);
  const widgetDraftsRef = useRef({});
  const widgetApiTimersRef = useRef({});
  const widgetPendingMetaRef = useRef({});
  const objectiveDirtyRef = useRef(false);
  const chatBoxRef = useRef(null);
  const chatStickToBottomRef = useRef(true);
  const lastChatRenderKeyRef = useRef('');
  const seenMarkTimerRef = useRef(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  ));
  const [mobileTab, setMobileTab] = useState('home');

  const visibleTools = useMemo(() => state?.tools || [], [state]);

  const applyAgentState = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    setState((prev) => {
      if (!prev) return data;
      const prevMessages = prev.chat_messages || [];
      const nextMessages = data.chat_messages || [];
      const sameMessages = (
        prevMessages.length === nextMessages.length &&
        prevMessages.every((item, idx) => {
          const other = nextMessages[idx];
          return (
            item?.id === other?.id &&
            item?.role === other?.role &&
            item?.content === other?.content &&
            item?.created_at === other?.created_at
          );
        })
      );
      return sameMessages ? { ...data, chat_messages: prevMessages } : data;
    });
    const serverObjective = data?.agent?.objective || '';
    setObjectiveDraft((prevDraft) => {
      if (objectiveDirtyRef.current) {
        if (prevDraft === serverObjective) {
          objectiveDirtyRef.current = false;
        }
        return prevDraft;
      }
      return serverObjective;
    });
  }, []);

  const loadState = useCallback(async (silent = false) => {
    try {
      const response = await axios.get(`${API_URL}/agents/${agentId}/state`);
      const data = response.data.data;
      applyAgentState(data);
      if (!silent) setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [agentId, applyAgentState]);

  useEffect(() => {
    let stream = null;
    let fallbackTimer = null;
    let cancelled = false;

    const startFallbackPolling = () => {
      if (fallbackTimer) return;
      fallbackTimer = setInterval(() => loadState(true), 4000);
    };

    loadState(true);
    try {
      stream = new EventSource(buildSseUrl(`/agents/${agentId}/stream`, accessToken));
      stream.addEventListener('state', (event) => {
        if (cancelled) return;
        try {
          const payload = JSON.parse(event.data);
          applyAgentState(payload);
          setAgentStreamState('live');
          setError('');
        } catch {
          setAgentStreamState('fallback');
        }
      });
      stream.addEventListener('gone', () => {
        if (cancelled) return;
        setError(t('agent.agentNotFound'));
        setAgentStreamState('fallback');
      });
      stream.onopen = () => {
        if (cancelled) return;
        setAgentStreamState('live');
      };
      stream.onerror = () => {
        if (cancelled) return;
        setAgentStreamState('fallback');
        startFallbackPolling();
      };
    } catch {
      setAgentStreamState('fallback');
      startFallbackPolling();
    }

    return () => {
      cancelled = true;
      if (stream) stream.close();
      if (fallbackTimer) clearInterval(fallbackTimer);
    };
  }, [agentId, applyAgentState, loadState, accessToken, t]);

  useEffect(() => {
    widgetDraftsRef.current = widgetDrafts;
  }, [widgetDrafts]);

  useEffect(() => {
    return () => {
      Object.values(widgetApiTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      widgetApiTimersRef.current = {};
      widgetPendingMetaRef.current = {};
    };
  }, []);

  useEffect(() => {
    const artifacts = state?.artifacts || [];
    const webApps = state?.web_apps || [];
    setWidgetDrafts((prev) => {
      const next = {};
      artifacts.forEach((artifact) => {
        const artifactType = normalizeArtifactType(artifact.artifact_type);
        if (artifactType === 'webform') {
          const inlineWidget = parseWebFormArtifact(artifact.content);
          if (!inlineWidget) return;
          const app = webApps.find((item) => {
            if (inlineWidget.appId && Number(item?.id) === Number(inlineWidget.appId)) return true;
            if (inlineWidget.appCode && String(item?.app_code || '').trim() === inlineWidget.appCode) return true;
            return false;
          });
          const schema = normalizeInlineWebFormSchema(app?.form_schema);
          const defaults = buildInlineWebFormDefaults(schema);
          const serverValues = (artifact.user_state && typeof artifact.user_state === 'object')
            ? artifact.user_state
            : {};
          next[artifact.id] = { ...defaults, ...serverValues, ...(prev[artifact.id] || {}) };
          return;
        }
        if (artifactType !== 'widget') return;
        const definition = parseWidgetDefinition(artifact.content);
        if (!definition) return;
        const defaults = buildWidgetDefaults(definition);
        const serverValues = (artifact.user_state && typeof artifact.user_state === 'object')
          ? artifact.user_state
          : {};
        // Server-authoritative widget state:
        // whenever backend pushes values, UI reflects them directly.
        const merged = { ...defaults, ...serverValues };
        const normalized = {};
        definition.fields.forEach((field) => {
          let value = normalizeWidgetFieldValue(field, merged[field.id]);
          if (field.type === 'table_editor') {
            const rows = Array.isArray(value) ? value.map((row) => ({
              _row_id: String(row?._row_id || '').trim() || buildWidgetRowId(),
              ...row
            })) : [];
            const minRows = Number(field.minRows);
            if (Number.isFinite(minRows) && minRows > 0) {
              while (rows.length < minRows) {
                rows.push(createWidgetEmptyTableRow(field));
              }
            }
            value = rows;
          }
          normalized[field.id] = value;
        });
        next[artifact.id] = normalized;
      });
      return next;
    });
  }, [state?.artifacts, state?.web_apps]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (seenMarkTimerRef.current) {
        clearTimeout(seenMarkTimerRef.current);
        seenMarkTimerRef.current = null;
      }
    };
  }, []);

  const safeAction = async (fn) => {
    try {
      setBusy(true);
      await fn();
      await loadState();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const updateObjective = () =>
    safeAction(async () => {
      await axios.patch(`${API_URL}/agents/${agentId}/objective`, { objective: objectiveDraft });
      objectiveDirtyRef.current = false;
    });

  const triggerRound = () =>
    safeAction(async () => {
      await axios.post(`${API_URL}/agents/${agentId}/events/trigger`, { reason: 'manual_ui_trigger' });
    });

  const sendChat = async (e) => {
    e.preventDefault();
    const message = chatInput.trim();
    if (!message) return;

    const optimisticId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimisticMessage = {
      id: optimisticId,
      role: 'user',
      content: message,
      created_at: new Date().toISOString()
    };

    setState((prev) => (
      prev ? { ...prev, chat_messages: [...(prev.chat_messages || []), optimisticMessage] } : prev
    ));
    setChatInput('');
    setOptimisticChatPending(true);

    try {
      await axios.post(`${API_URL}/agents/${agentId}/chat`, { message });
      // Keep the UI reactive: SSE (or fallback polling) will reconcile state.
      loadState(true).catch(() => {});
    } catch (err) {
      setState((prev) => (
        prev
          ? {
            ...prev,
            chat_messages: (prev.chat_messages || []).filter((item) => item.id !== optimisticId)
          }
          : prev
      ));
      setOptimisticChatPending(false);
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const answerQuestion = (question) =>
    safeAction(async () => {
      const questionMode = resolveQuestionInputMode(question);
      const isMulti = questionMode === 'multi_closed';
      const rawValue = questionDrafts[question.id];
      const value = isMulti
        ? (Array.isArray(rawValue) ? rawValue.filter((item) => String(item || '').trim()) : [])
        : rawValue;
      if (isMulti) {
        if (!Array.isArray(value) || value.length === 0) return;
      } else if (value === undefined || value === '') {
        return;
      }
      await axios.post(`${API_URL}/agents/${agentId}/questions/${question.id}/answer`, {
        answer: value
      });
      setQuestionDrafts((prev) => ({ ...prev, [question.id]: isMulti ? [] : '' }));
    });

  const toggleMultiQuestionOption = useCallback((questionId, option, checked) => {
    setQuestionDrafts((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      if (checked) {
        if (current.includes(option)) return prev;
        return { ...prev, [questionId]: [...current, option] };
      }
      return { ...prev, [questionId]: current.filter((item) => item !== option) };
    });
  }, []);

  const isQuestionDraftEmpty = useCallback((question) => {
    const questionMode = resolveQuestionInputMode(question);
    const value = questionDrafts[question.id];
    if (questionMode === 'multi_closed') {
      return !Array.isArray(value) || value.length === 0;
    }
    return value === undefined || value === '';
  }, [questionDrafts]);

  const addTool = (e) => {
    e.preventDefault();
    const serverInput = toolForm.mcp_server.trim();
    const needsName = !isCaptainCode(serverInput);
    const authType = String(toolForm.auth_type || 'none').trim().toLowerCase();
    const authHeaderName = toolForm.auth_header_name.trim();
    const authToken = toolForm.auth_token.trim();
    if (!serverInput) return;
    if (needsName && !toolForm.name.trim()) return;
    if (needsName && authType === 'header' && !authHeaderName) return;
    if (needsName && authType !== 'none' && !authToken) return;
    safeAction(async () => {
      await axios.post(`${API_URL}/agents/${agentId}/tools`, {
        name: toolForm.name.trim(),
        mcp_server: serverInput,
        description: '',
        input_schema: {},
        auth_type: needsName ? authType : 'none',
        auth_header_name: needsName ? authHeaderName : '',
        auth_token: needsName ? authToken : '',
        tool_request_id: null
      });
      setToolForm({
        name: '',
        mcp_server: '',
        auth_type: 'none',
        auth_header_name: '',
        auth_token: ''
      });
      setShowToolCreate(false);
    });
  };

  const removeTool = (toolId) =>
    safeAction(async () => {
      await axios.delete(`${API_URL}/agents/${agentId}/tools/${toolId}`);
    });

  const resolveToolRequest = (requestId, action) =>
    safeAction(async () => {
      await axios.post(`${API_URL}/agents/${agentId}/tool-requests/${requestId}/resolve`, {
        action,
        decline_reason: action === 'declined' ? 'Declined by user' : null
      });
    });

  const addDocument = (e) => {
    e.preventDefault();
    if (!docForm.url.trim()) return;
    safeAction(async () => {
      const name = docForm.name.trim() || docForm.url.trim();
      await axios.post(`${API_URL}/agents/${agentId}/documents`, {
        name,
        doc_type: inferDocTypeFromInput(`${name} ${docForm.url}`),
        url: docForm.url.trim(),
        notes: ''
      });
      setDocForm({ name: '', url: '' });
      setShowDocLinkForm(false);
      setShowDocActions(false);
    });
  };

  const removeDocument = (docId) =>
    safeAction(async () => {
      await axios.delete(`${API_URL}/agents/${agentId}/documents/${docId}`);
    });

  const removeMemoryEvent = (memoryEventId) =>
    safeAction(async () => {
      await axios.delete(`${API_URL}/agents/${agentId}/memory-events/${memoryEventId}`);
    });

  const queueWidgetUpdate = useCallback((artifactId, values, metadata = {}) => {
    const previousTimer = widgetApiTimersRef.current[artifactId];
    if (previousTimer) clearTimeout(previousTimer);

    const changedFields = Array.isArray(metadata.changedFields)
      ? metadata.changedFields.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
    const currentMeta = widgetPendingMetaRef.current[artifactId] || {
      changedFields: [],
      clientTimestamp: null
    };
    const mergedChangedFields = Array.from(new Set([...(currentMeta.changedFields || []), ...changedFields]));
    const clientTimestamp = (
      String(metadata.clientTimestamp || '').trim() ||
      String(currentMeta.clientTimestamp || '').trim() ||
      new Date().toISOString()
    );
    widgetPendingMetaRef.current[artifactId] = {
      changedFields: mergedChangedFields,
      clientTimestamp
    };

    widgetApiTimersRef.current[artifactId] = setTimeout(async () => {
      try {
        const latest = widgetDraftsRef.current[artifactId] || values || {};
        const pending = widgetPendingMetaRef.current[artifactId] || {};
        await axios.post(`${API_URL}/agents/${agentId}/artifacts/${artifactId}/widget-input`, {
          values: latest,
          submit_mode: 'auto',
          changed_fields: Array.isArray(pending.changedFields) ? pending.changedFields : [],
          client_timestamp: String(pending.clientTimestamp || '').trim() || new Date().toISOString()
        });
        delete widgetPendingMetaRef.current[artifactId];
      } catch (err) {
        setError(err?.response?.data?.detail || err.message);
      } finally {
        delete widgetApiTimersRef.current[artifactId];
      }
    }, 350);
  }, [agentId]);

  const handleWidgetFieldChange = useCallback((artifact, field, rawValue, options = {}) => {
    const artifactId = artifact?.id;
    if (!artifactId || !field?.id) return;
    const submitBehavior = options.submitBehavior === 'explicit_submit' ? 'explicit_submit' : 'auto_debounce';
    const normalizedValue = normalizeWidgetFieldValue(field, rawValue);
    setWidgetDrafts((prev) => {
      const artifactValues = { ...(prev[artifactId] || {}), [field.id]: normalizedValue };
      const next = { ...prev, [artifactId]: artifactValues };
      widgetDraftsRef.current = next;
      if (submitBehavior === 'auto_debounce') {
        queueWidgetUpdate(artifactId, artifactValues, {
          changedFields: [field.id],
          clientTimestamp: new Date().toISOString()
        });
      }
      return next;
    });
  }, [queueWidgetUpdate]);

  const handleWidgetTableAddRow = useCallback((artifact, field, submitBehavior = 'auto_debounce') => {
    const artifactId = artifact?.id;
    if (!artifactId || !field?.id) return;
    setWidgetDrafts((prev) => {
      const previousRows = Array.isArray(prev?.[artifactId]?.[field.id]) ? prev[artifactId][field.id] : [];
      const maxRows = Number(field.maxRows);
      if (Number.isFinite(maxRows) && previousRows.length >= maxRows) return prev;
      const nextRows = [...previousRows, createWidgetEmptyTableRow(field)];
      const artifactValues = { ...(prev[artifactId] || {}), [field.id]: nextRows };
      const next = { ...prev, [artifactId]: artifactValues };
      widgetDraftsRef.current = next;
      if (submitBehavior === 'auto_debounce') {
        queueWidgetUpdate(artifactId, artifactValues, {
          changedFields: [field.id],
          clientTimestamp: new Date().toISOString()
        });
      }
      return next;
    });
  }, [queueWidgetUpdate]);

  const handleWidgetTableRemoveRow = useCallback((artifact, field, rowId, submitBehavior = 'auto_debounce') => {
    const artifactId = artifact?.id;
    if (!artifactId || !field?.id || !rowId) return;
    setWidgetDrafts((prev) => {
      const previousRows = Array.isArray(prev?.[artifactId]?.[field.id]) ? prev[artifactId][field.id] : [];
      const nextRows = previousRows.filter((row) => String(row?._row_id || '') !== String(rowId));
      const artifactValues = { ...(prev[artifactId] || {}), [field.id]: nextRows };
      const next = { ...prev, [artifactId]: artifactValues };
      widgetDraftsRef.current = next;
      if (submitBehavior === 'auto_debounce') {
        queueWidgetUpdate(artifactId, artifactValues, {
          changedFields: [field.id],
          clientTimestamp: new Date().toISOString()
        });
      }
      return next;
    });
  }, [queueWidgetUpdate]);

  const handleWidgetTableCellChange = useCallback(
    (artifact, field, rowId, column, rawValue, submitBehavior = 'auto_debounce') => {
      const artifactId = artifact?.id;
      if (!artifactId || !field?.id || !rowId || !column?.id) return;
      if (column.type === 'label' || column.editable === false) return;
      const normalizedCellValue = normalizeWidgetFieldValue(column, rawValue);
      setWidgetDrafts((prev) => {
        const previousRows = Array.isArray(prev?.[artifactId]?.[field.id]) ? prev[artifactId][field.id] : [];
        const nextRows = previousRows.map((row) => {
          if (String(row?._row_id || '') !== String(rowId)) return row;
          return { ...row, [column.id]: normalizedCellValue };
        });
        const artifactValues = { ...(prev[artifactId] || {}), [field.id]: nextRows };
        const next = { ...prev, [artifactId]: artifactValues };
        widgetDraftsRef.current = next;
        if (submitBehavior === 'auto_debounce') {
          queueWidgetUpdate(artifactId, artifactValues, {
            changedFields: [field.id],
            clientTimestamp: new Date().toISOString()
          });
        }
        return next;
      });
    },
    [queueWidgetUpdate]
  );

  const submitWidgetArtifact = useCallback(async (artifact, widgetDefinition) => {
    const artifactId = artifact?.id;
    if (!artifactId || !widgetDefinition?.fields) return;
    const previousTimer = widgetApiTimersRef.current[artifactId];
    if (previousTimer) clearTimeout(previousTimer);
    delete widgetApiTimersRef.current[artifactId];
    delete widgetPendingMetaRef.current[artifactId];

    const values = widgetDraftsRef.current[artifactId] || buildWidgetDefaults(widgetDefinition);
    const changedFields = widgetDefinition.fields.map((field) => field.id);
    setWidgetSubmitting((prev) => ({ ...prev, [artifactId]: true }));
    try {
      await axios.post(`${API_URL}/agents/${agentId}/artifacts/${artifactId}/widget-input`, {
        values,
        submit_mode: 'submit',
        changed_fields: changedFields,
        client_timestamp: new Date().toISOString()
      });
      setError('');
      loadState(true).catch(() => {});
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setWidgetSubmitting((prev) => {
        const next = { ...prev };
        delete next[artifactId];
        return next;
      });
    }
  }, [agentId, loadState]);

  const resolveInlineWebFormContext = useCallback((inlineWidget) => {
    if (!inlineWidget) return null;
    const webApps = state?.web_apps || [];
    const app = webApps.find((item) => {
      if (inlineWidget.appId && Number(item?.id) === Number(inlineWidget.appId)) return true;
      if (inlineWidget.appCode && String(item?.app_code || '').trim() === inlineWidget.appCode) return true;
      return false;
    });
    if (!app) return null;
    const schema = normalizeInlineWebFormSchema(app.form_schema);
    const links = Array.isArray(app.links) ? app.links : [];
    const link = links.find((item) => String(item?.link_code || '').trim() === inlineWidget.linkCode) || null;
    return { app, schema, link };
  }, [state?.web_apps]);

  const handleInlineWebFormFieldChange = useCallback((artifactId, field, rawValue) => {
    if (!artifactId || !field?.id) return;
    const normalizedValue = normalizeInlineWebFormFieldValue(field, rawValue);
    setWidgetDrafts((prev) => {
      const artifactValues = { ...(prev[artifactId] || {}), [field.id]: normalizedValue };
      const next = { ...prev, [artifactId]: artifactValues };
      widgetDraftsRef.current = next;
      return next;
    });
  }, []);

  const submitInlineWebForm = useCallback(async (artifact, inlineWidget) => {
    const artifactId = artifact?.id;
    if (!artifactId || !inlineWidget?.linkCode) return;

    const context = resolveInlineWebFormContext(inlineWidget);
    if (!context) {
      setError(t('agent.inlineWebFormUnavailable'));
      return;
    }

    const schema = context.schema;
    const currentDraft = widgetDraftsRef.current[artifactId] || {};
    const payload = {};
    const missingRequired = [];
    (schema.fields || []).forEach((field) => {
      const normalized = normalizeInlineWebFormFieldValue(field, currentDraft[field.id]);
      if (field.type === 'number' && normalized === '') {
        payload[field.id] = null;
      } else {
        payload[field.id] = normalized;
      }
      if (isInlineWebFormFieldEmpty(field, normalized)) {
        missingRequired.push(field.id);
      }
    });
    if (missingRequired.length > 0) {
      setError(t('agent.inlineWebFormRequired'));
      return;
    }

    setInlineSubmitting((prev) => ({ ...prev, [artifactId]: true }));
    try {
      const response = await axios.post(`${API_URL}/webform/${inlineWidget.linkCode}/submit`, {
        values: payload
      });
      const data = response?.data?.data || {};
      const responsePayload = data?.response && typeof data.response === 'object' ? data.response : {};
      const message = String(
        responsePayload.message ||
        inlineWidget.successMessage ||
        schema.successMessage ||
        t('agent.inlineWebFormSubmitted')
      ).trim();
      setWidgetDrafts((prev) => {
        const artifactValues = {
          ...(prev[artifactId] || {}),
          ...payload,
          __inline_submitted: true,
          __inline_message: message,
          __inline_status: String(responsePayload.status || 'ok').toLowerCase(),
          __inline_submitted_at: new Date().toISOString()
        };
        const next = { ...prev, [artifactId]: artifactValues };
        widgetDraftsRef.current = next;
        return next;
      });
      loadState(true).catch(() => {});
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setInlineSubmitting((prev) => {
        const next = { ...prev };
        delete next[artifactId];
        return next;
      });
    }
  }, [loadState, resolveInlineWebFormContext, t]);

  const openUploadDialog = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await safeAction(async () => {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/agents/${agentId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowDocActions(false);
      setShowDocLinkForm(false);
    });
  };

  const toggleToolCreate = () => {
    setShowToolCreate((previous) => !previous);
  };

  const toggleDocActions = () => {
    setShowDocActions((previous) => {
      const next = !previous;
      if (!next) setShowDocLinkForm(false);
      return next;
    });
  };

  const showDocLinkCreator = () => {
    setShowDocActions(true);
    setShowDocLinkForm(true);
  };

  const publishTemplate = (e) => {
    e.preventDefault();
    safeAction(async () => {
      const response = await axios.post(`${API_URL}/agents/${agentId}/templates/publish`, {
        visibility: String(templatePublishForm.visibility || 'public'),
        name: String(templatePublishForm.name || '').trim(),
        description: String(templatePublishForm.description || '').trim()
      });
      setTemplatePublishResult(response?.data?.data || null);
      setShowTemplatePublish(true);
    });
  };

  const copyTemplateShareLink = useCallback(async () => {
    const sharePath = String(templatePublishResult?.share_path || '').trim();
    const fallback = String(templatePublishResult?.share_url || '').trim();
    const url = (sharePath && typeof window !== 'undefined')
      ? `${window.location.origin}${sharePath}`
      : fallback;
    if (!url || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API may be blocked by browser context.
    }
  }, [templatePublishResult]);

  const markSeenChannels = useCallback(async (channels) => {
    const normalized = Array.from(new Set(
      (channels || []).map((item) => String(item || '').trim()).filter(Boolean)
    ));
    if (!normalized.length) return;
    try {
      const response = await axios.post(`${API_URL}/agents/${agentId}/ui/seen`, { channels: normalized });
      const uiPayload = response?.data?.data;
      if (uiPayload && typeof uiPayload === 'object') {
        setState((prev) => (prev ? { ...prev, ui: uiPayload } : prev));
      }
    } catch (err) {
      // Keep UX non-blocking for read markers.
      console.warn('ui/seen failed', err?.response?.status || err?.message || err);
    }
  }, [agentId]);

  const openQuestions = (state?.questions || []).filter((q) => q.status === 'open');
  const unreadTabs = (state?.ui?.unread?.tabs && typeof state.ui.unread.tabs === 'object')
    ? state.ui.unread.tabs
    : {};
  const isPausedByCredits = Boolean(state?.runtime?.paused_by_credits);
  const isThinking = Boolean(state?.runtime?.is_thinking) && !isPausedByCredits;
  const effectiveIsThinking = (isThinking || optimisticChatPending) && !isPausedByCredits;
  const activeEventType = state?.runtime?.active_event_type || null;
  const runtimeCreditsLabel = state?.runtime?.credits_unlimited
    ? t('credits.unlimited')
    : t('credits.remaining', { value: formatCredits(state?.runtime?.credits_remaining || 0) });
  const runtimeCreditsCompact = state?.runtime?.credits_unlimited
    ? t('credits.shortUnlimited')
    : t('credits.shortRemaining', { value: formatCredits(state?.runtime?.credits_remaining || 0) });
  const templateShareUrl = (() => {
    const sharePath = String(templatePublishResult?.share_path || '').trim();
    if (sharePath && typeof window !== 'undefined') {
      return `${window.location.origin}${sharePath}`;
    }
    return String(templatePublishResult?.share_url || '').trim();
  })();
  const chatMessages = state?.chat_messages || [];
  let lastUserIdx = -1;
  let lastAssistantIdx = -1;
  chatMessages.forEach((msg, idx) => {
    if (msg?.role === 'user') lastUserIdx = idx;
    if (msg?.role === 'assistant') lastAssistantIdx = idx;
  });
  const lastChatMessageId = chatMessages.length ? chatMessages[chatMessages.length - 1]?.id : null;
  const hasPendingUserMessage = lastUserIdx > lastAssistantIdx;
  const isChatTyping = effectiveIsThinking && hasPendingUserMessage;
  const isMobileChatActive = isMobileViewport && mobileTab === 'chat';

  useEffect(() => {
    if (!hasPendingUserMessage) {
      setOptimisticChatPending(false);
    }
  }, [hasPendingUserMessage]);

  const handleChatScroll = useCallback(() => {
    const box = chatBoxRef.current;
    if (!box) return;
    const distanceToBottom = box.scrollHeight - box.scrollTop - box.clientHeight;
    chatStickToBottomRef.current = distanceToBottom <= 48;
  }, []);

  useEffect(() => {
    const box = chatBoxRef.current;
    if (!box) return;
    const renderKey = `${chatMessages.length}:${String(lastChatMessageId || 'none')}:${isChatTyping ? 'typing' : 'idle'}`;
    if (renderKey === lastChatRenderKeyRef.current) return;
    lastChatRenderKeyRef.current = renderKey;
    if (!chatStickToBottomRef.current) return;
    requestAnimationFrame(() => {
      const target = chatBoxRef.current;
      if (!target) return;
      target.scrollTop = target.scrollHeight;
    });
  }, [chatMessages.length, lastChatMessageId, isChatTyping]);

  useEffect(() => {
    if (!state?.agent?.id) return;
    const unreadSections = (
      state?.ui?.unread?.sections && typeof state.ui.unread.sections === 'object'
    ) ? state.ui.unread.sections : {};

    let channels = [];
    let pendingUnread = 0;
    if (isMobileViewport) {
      channels = MOBILE_TAB_CHANNELS[mobileTab] || [];
      pendingUnread = channels.reduce((sum, channel) => sum + Number(unreadSections[channel] || 0), 0);
    } else {
      channels = ALL_VIEW_CHANNELS;
      pendingUnread = Number(state?.ui?.unread?.total || 0);
    }
    if (pendingUnread <= 0) return;
    if (seenMarkTimerRef.current) {
      clearTimeout(seenMarkTimerRef.current);
      seenMarkTimerRef.current = null;
    }
    seenMarkTimerRef.current = setTimeout(() => {
      markSeenChannels(channels);
    }, isMobileViewport ? 350 : 80);
    return () => {
      if (seenMarkTimerRef.current) {
        clearTimeout(seenMarkTimerRef.current);
        seenMarkTimerRef.current = null;
      }
    };
  }, [
    state?.agent?.id,
    state?.ui?.unread?.total,
    state?.ui?.unread?.sections,
    isMobileViewport,
    mobileTab,
    markSeenChannels
  ]);

  if (!state) {
    return (
      <div className="window">
        <div className="topbar">
          <div className="dots"><span /><span /><span /></div>
          <div className="top-title">{t('agent.workspace')}</div>
          <Link to="/" className="top-link">{t('common.back')}</Link>
        </div>
        <div className="page">
          <header className="hero">
            <h1>{t('agent.workspace')}</h1>
            <p>{error || t('common.loading')}</p>
          </header>
        </div>
      </div>
    );
  }

  const renderInlineWebFormArtifact = (artifact, inlineWidget) => {
    const context = resolveInlineWebFormContext(inlineWidget);
    if (!context) {
      return <p className="artifact-fallback">{t('agent.inlineWebFormUnavailable')}</p>;
    }
    const schema = context.schema;
    const values = widgetDrafts[artifact.id] || buildInlineWebFormDefaults(schema);
    const submitting = Boolean(inlineSubmitting[artifact.id]);
    const linkInactive = context.link && String(context.link.status || '').toLowerCase() !== 'active';
    const submitted = Boolean(values.__inline_submitted) || Boolean(linkInactive);
    const successMessage = String(
      values.__inline_message ||
      inlineWidget.successMessage ||
      schema.successMessage ||
      t('agent.inlineWebFormSubmitted')
    ).trim();

    if (submitted) {
      return (
        <div className="inline-webform-result">
          <span className="status-chip idle">{t('agent.inlineWebFormDone')}</span>
          <p>{successMessage || t('agent.inlineWebFormSubmitted')}</p>
        </div>
      );
    }

    const hasMissingRequired = (schema.fields || []).some((field) => {
      const normalized = normalizeInlineWebFormFieldValue(field, values[field.id]);
      return isInlineWebFormFieldEmpty(field, normalized);
    });

    return (
      <form
        className="artifact-inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          submitInlineWebForm(artifact, inlineWidget);
        }}
      >
        {(inlineWidget.description || schema.description)
          ? <p className="artifact-widget-description">{inlineWidget.description || schema.description}</p>
          : null}
        <div className="artifact-widget-fields">
          {(schema.fields || []).map((field) => {
            const currentValue = values[field.id];
            if (field.type === 'textarea') {
              return (
                <label key={field.id} className="artifact-widget-field">
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                  <textarea
                    value={String(currentValue ?? '')}
                    placeholder={field.placeholder || ''}
                    onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.value)}
                  />
                </label>
              );
            }
            if (field.type === 'number') {
              return (
                <label key={field.id} className="artifact-widget-field">
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                  <input
                    type="number"
                    min={Number.isFinite(field.min) ? field.min : undefined}
                    max={Number.isFinite(field.max) ? field.max : undefined}
                    step={Number.isFinite(field.step) ? field.step : undefined}
                    value={currentValue === '' || currentValue === null || currentValue === undefined ? '' : Number(currentValue)}
                    placeholder={field.placeholder || ''}
                    onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.value)}
                  />
                </label>
              );
            }
            if (field.type === 'date') {
              return (
                <label key={field.id} className="artifact-widget-field">
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                  <input
                    type="date"
                    value={String(currentValue ?? '')}
                    onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.value)}
                  />
                </label>
              );
            }
            if (field.type === 'select') {
              const options = Array.isArray(field.options) ? field.options : [];
              return (
                <label key={field.id} className="artifact-widget-field">
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                  <select
                    value={String(currentValue ?? options[0]?.value ?? '')}
                    onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.value)}
                  >
                    {options.map((option) => (
                      <option key={`${field.id}-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              );
            }
            if (field.type === 'multi_select') {
              const selected = Array.isArray(currentValue) ? currentValue : [];
              const options = Array.isArray(field.options) ? field.options : [];
              return (
                <div key={field.id} className="artifact-widget-field">
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                  <div className="multi-question-options">
                    {options.map((option) => (
                      <label key={`${field.id}-${option.value}`} className="multi-question-option">
                        <input
                          type="checkbox"
                          checked={selected.includes(option.value)}
                          onChange={(e) => {
                            const nextValues = e.target.checked
                              ? [...selected, option.value]
                              : selected.filter((item) => item !== option.value);
                            handleInlineWebFormFieldChange(artifact.id, field, nextValues);
                          }}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            }
            if (field.type === 'boolean') {
              return (
                <label key={field.id} className="multi-question-option">
                  <input
                    type="checkbox"
                    checked={Boolean(currentValue)}
                    onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.checked)}
                  />
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                </label>
              );
            }
            return (
              <label key={field.id} className="artifact-widget-field">
                <span>{field.label}{field.required ? ' *' : ''}</span>
                <input
                  type="text"
                  value={String(currentValue ?? '')}
                  placeholder={field.placeholder || ''}
                  onChange={(e) => handleInlineWebFormFieldChange(artifact.id, field, e.target.value)}
                />
              </label>
            );
          })}
        </div>
        <button type="submit" disabled={submitting || hasMissingRequired}>
          {submitting ? t('agent.inlineWebFormSubmitting') : schema.submitLabel}
        </button>
      </form>
    );
  };

  const renderWebFormArtifact = (artifact) => {
    const inlineWidget = parseWebFormArtifact(artifact.content);
    if (!inlineWidget) return <pre className="artifact-fallback">{artifact.content}</pre>;
    return renderInlineWebFormArtifact(artifact, inlineWidget);
  };

  const renderWidgetArtifact = (artifact) => {
    const widgetDefinition = parseWidgetDefinition(artifact.content);
    if (!widgetDefinition) return <pre className="artifact-fallback">{artifact.content}</pre>;

    const values = widgetDrafts[artifact.id] || buildWidgetDefaults(widgetDefinition);
    const isExplicitSubmit = widgetDefinition.submitBehavior === 'explicit_submit';
    const submitting = Boolean(widgetSubmitting[artifact.id]);

    return (
      <form
        className="artifact-widget"
        onSubmit={(e) => {
          e.preventDefault();
          if (!isExplicitSubmit || submitting) return;
          submitWidgetArtifact(artifact, widgetDefinition);
        }}
      >
        {widgetDefinition.description ? <p className="artifact-widget-description">{widgetDefinition.description}</p> : null}
        <div className="artifact-widget-fields">
          {widgetDefinition.fields.map((field) => {
            const isFieldEditable = field.editable !== false;
            const label = (
              <span>
                {field.label}
                {field.required ? ' *' : ''}
                {field.unit ? ` (${field.unit})` : ''}
              </span>
            );
            const hint = field.helpText ? <span className="meta">{field.helpText}</span> : null;

            if (field.type === 'label') {
              const text = String(values[field.id] ?? field.default ?? '').trim();
              return (
                <div key={field.id} className="artifact-widget-field artifact-widget-label">
                  {field.label ? <strong>{field.label}</strong> : null}
                  {text ? <p>{text}</p> : null}
                  {hint}
                </div>
              );
            }

            if (field.type === 'textarea') {
              return (
                <label key={field.id} className="artifact-widget-field">
                  {label}
                  <textarea
                    value={String(values[field.id] ?? '')}
                    placeholder={field.placeholder || ''}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  {hint}
                </label>
              );
            }

            if (field.type === 'slider') {
              const currentValue = Number(values[field.id] ?? field.default ?? field.min ?? 0);
              return (
                <label key={field.id} className="artifact-widget-field">
                  <span>{field.label}: <strong>{currentValue}</strong></span>
                  <input
                    type="range"
                    min={Number(field.min ?? 0)}
                    max={Number(field.max ?? 100)}
                    step={Number(field.step ?? 1)}
                    value={currentValue}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  {hint}
                </label>
              );
            }

            if (field.type === 'integer' || field.type === 'decimal') {
              const numeric = values[field.id];
              return (
                <label key={field.id} className="artifact-widget-field">
                  {label}
                  <input
                    type="number"
                    min={Number.isFinite(field.min) ? field.min : undefined}
                    max={Number.isFinite(field.max) ? field.max : undefined}
                    step={Number.isFinite(field.step) ? field.step : undefined}
                    value={numeric === null || numeric === undefined ? '' : Number(numeric)}
                    placeholder={field.placeholder || ''}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  {hint}
                </label>
              );
            }

            if (field.type === 'select' || field.type === 'radio') {
              const options = Array.isArray(field.options) ? field.options : [];
              return (
                <label key={field.id} className="artifact-widget-field">
                  {label}
                  <select
                    value={String(values[field.id] ?? options[0]?.value ?? '')}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  >
                    {options.map((option) => (
                      <option key={`${field.id}-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {hint}
                </label>
              );
            }

            if (field.type === 'multi_select') {
              const selected = Array.isArray(values[field.id]) ? values[field.id] : [];
              const options = Array.isArray(field.options) ? field.options : [];
              return (
                <div key={field.id} className="artifact-widget-field">
                  {label}
                  <div className="multi-question-options">
                    {options.map((option) => (
                      <label key={`${field.id}-${option.value}`} className="multi-question-option">
                        <input
                          type="checkbox"
                          checked={selected.includes(option.value)}
                          disabled={!isFieldEditable || submitting}
                          onChange={(e) => {
                            const nextValues = e.target.checked
                              ? [...selected, option.value]
                              : selected.filter((item) => item !== option.value);
                            handleWidgetFieldChange(artifact, field, nextValues, { submitBehavior: widgetDefinition.submitBehavior });
                          }}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {hint}
                </div>
              );
            }

            if (field.type === 'boolean') {
              return (
                <label key={field.id} className="multi-question-option artifact-widget-boolean">
                  <input
                    type="checkbox"
                    checked={Boolean(values[field.id])}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.checked, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  <span>{field.label}{field.required ? ' *' : ''}</span>
                </label>
              );
            }

            if (field.type === 'tags') {
              const list = Array.isArray(values[field.id]) ? values[field.id] : [];
              return (
                <label key={field.id} className="artifact-widget-field">
                  {label}
                  <input
                    type="text"
                    value={list.join(', ')}
                    placeholder={field.placeholder || t('agent.widgetTagsPlaceholder')}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  {hint}
                </label>
              );
            }

            if (field.type === 'date' || field.type === 'time' || field.type === 'datetime') {
              const inputType = field.type === 'datetime' ? 'datetime-local' : field.type;
              return (
                <label key={field.id} className="artifact-widget-field">
                  {label}
                  <input
                    type={inputType}
                    value={String(values[field.id] ?? '')}
                    disabled={!isFieldEditable || submitting}
                    onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                  />
                  {hint}
                </label>
              );
            }

            if (field.type === 'table_editor') {
              const rows = Array.isArray(values[field.id]) ? values[field.id] : [];
              const maxRows = Number(field.maxRows);
              const minRows = Number(field.minRows);
              const canAddRow = isFieldEditable && !submitting && (!Number.isFinite(maxRows) || rows.length < maxRows);
              const canDeleteAny = isFieldEditable && !submitting && field.allowRowDelete !== false && (!Number.isFinite(minRows) || rows.length > minRows);
              return (
                <div key={field.id} className="artifact-widget-field artifact-widget-table">
                  <div className="artifact-widget-table-head">
                    {label}
                    <button
                      type="button"
                      className="artifact-widget-table-add-btn"
                      onClick={() => handleWidgetTableAddRow(artifact, field, widgetDefinition.submitBehavior)}
                      disabled={!canAddRow}
                    >
                      {t('agent.widgetAddRow')}
                    </button>
                  </div>
                  {rows.length === 0 ? (
                    <p className="muted">{t('agent.widgetNoRows')}</p>
                  ) : (
                    <div className="artifact-widget-table-scroll">
                      <table className="artifact-widget-table-grid">
                        <thead>
                          <tr>
                            {field.columns.map((column) => (
                              <th key={`${field.id}-${column.id}`}>{column.label}</th>
                            ))}
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, rowIndex) => (
                            <tr key={String(row._row_id || `${field.id}-${rowIndex}`)}>
                              {field.columns.map((column) => {
                                const cellValue = row[column.id];
                                const isColumnEditable = isFieldEditable && column.editable !== false && column.type !== 'label';
                                if (column.type === 'label') {
                                  const labelValue = String(cellValue ?? column.default ?? '').trim();
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <div className="artifact-widget-table-label-cell">
                                        {labelValue || '—'}
                                      </div>
                                    </td>
                                  );
                                }
                                if (column.type === 'select' || column.type === 'radio') {
                                  const options = Array.isArray(column.options) ? column.options : [];
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <select
                                        value={String(cellValue ?? options[0]?.value ?? '')}
                                        disabled={!isColumnEditable || submitting}
                                        onChange={(e) => handleWidgetTableCellChange(
                                          artifact,
                                          field,
                                          row._row_id,
                                          column,
                                          e.target.value,
                                          widgetDefinition.submitBehavior
                                        )}
                                      >
                                        {options.map((option) => (
                                          <option key={`${column.id}-${option.value}`} value={option.value}>{option.label}</option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                }
                                if (column.type === 'boolean') {
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(cellValue)}
                                        disabled={!isColumnEditable || submitting}
                                        onChange={(e) => handleWidgetTableCellChange(
                                          artifact,
                                          field,
                                          row._row_id,
                                          column,
                                          e.target.checked,
                                          widgetDefinition.submitBehavior
                                        )}
                                      />
                                    </td>
                                  );
                                }
                                if (column.type === 'multi_select' || column.type === 'tags') {
                                  const asText = Array.isArray(cellValue) ? cellValue.join(', ') : String(cellValue ?? '');
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <input
                                        type="text"
                                        value={asText}
                                        placeholder={column.placeholder || ''}
                                        disabled={!isColumnEditable || submitting}
                                        onChange={(e) => handleWidgetTableCellChange(
                                          artifact,
                                          field,
                                          row._row_id,
                                          column,
                                          e.target.value,
                                          widgetDefinition.submitBehavior
                                        )}
                                      />
                                    </td>
                                  );
                                }
                                if (column.type === 'integer' || column.type === 'decimal') {
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <input
                                        type="number"
                                        min={Number.isFinite(column.min) ? column.min : undefined}
                                        max={Number.isFinite(column.max) ? column.max : undefined}
                                        step={Number.isFinite(column.step) ? column.step : undefined}
                                        value={cellValue === null || cellValue === undefined ? '' : Number(cellValue)}
                                        disabled={!isColumnEditable || submitting}
                                        onChange={(e) => handleWidgetTableCellChange(
                                          artifact,
                                          field,
                                          row._row_id,
                                          column,
                                          e.target.value,
                                          widgetDefinition.submitBehavior
                                        )}
                                      />
                                    </td>
                                  );
                                }
                                if (column.type === 'textarea') {
                                  return (
                                    <td key={`${row._row_id}-${column.id}`}>
                                      <textarea
                                        value={String(cellValue ?? '')}
                                        placeholder={column.placeholder || ''}
                                        disabled={!isColumnEditable || submitting}
                                        onChange={(e) => handleWidgetTableCellChange(
                                          artifact,
                                          field,
                                          row._row_id,
                                          column,
                                          e.target.value,
                                          widgetDefinition.submitBehavior
                                        )}
                                      />
                                    </td>
                                  );
                                }
                                const inputType = column.type === 'date' || column.type === 'time'
                                  ? column.type
                                  : column.type === 'datetime'
                                    ? 'datetime-local'
                                    : 'text';
                                return (
                                  <td key={`${row._row_id}-${column.id}`}>
                                    <input
                                      type={inputType}
                                      value={String(cellValue ?? '')}
                                      placeholder={column.placeholder || ''}
                                      disabled={!isColumnEditable || submitting}
                                      onChange={(e) => handleWidgetTableCellChange(
                                        artifact,
                                        field,
                                        row._row_id,
                                        column,
                                        e.target.value,
                                        widgetDefinition.submitBehavior
                                      )}
                                    />
                                  </td>
                                );
                              })}
                              <td className="artifact-widget-table-actions">
                                <button
                                  type="button"
                                  className="row-icon-btn row-icon-btn--danger"
                                  onClick={() => handleWidgetTableRemoveRow(
                                    artifact,
                                    field,
                                    row._row_id,
                                    widgetDefinition.submitBehavior
                                  )}
                                  disabled={!canDeleteAny}
                                  aria-label={t('agent.widgetRemoveRow')}
                                  title={t('agent.widgetRemoveRow')}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {hint}
                </div>
              );
            }

            return (
              <label key={field.id} className="artifact-widget-field">
                {label}
                <input
                  type="text"
                  value={String(values[field.id] ?? '')}
                  placeholder={field.placeholder || ''}
                  disabled={!isFieldEditable || submitting}
                  onChange={(e) => handleWidgetFieldChange(artifact, field, e.target.value, { submitBehavior: widgetDefinition.submitBehavior })}
                />
                {hint}
              </label>
            );
          })}
        </div>
        {isExplicitSubmit ? (
          <>
            <button type="submit" disabled={submitting}>
              {submitting ? t('agent.widgetSubmitting') : t('agent.widgetSubmit')}
            </button>
            <span className="meta">{t('agent.widgetSubmitHint')}</span>
          </>
        ) : (
          <span className="meta">{t('agent.widgetDebounceHint')}</span>
        )}
      </form>
    );
  };

  const widgetArtifactHasTableEditor = (artifact) => {
    if (!artifact) return false;
    const definition = parseWidgetDefinition(artifact.content);
    if (!definition || !Array.isArray(definition.fields)) return false;
    return definition.fields.some((field) => String(field?.type || '').trim() === 'table_editor');
  };

  const formatUnreadBadge = (count) => {
    const value = Number(count || 0);
    if (value <= 0) return '';
    if (value > 99) return '99+';
    return String(value);
  };

  const renderMobileTabIcon = (tabId) => {
    if (tabId === 'home') {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4.7a.3.3 0 0 1-.3-.3V15a2 2 0 0 0-4 0v5.7a.3.3 0 0 1-.3.3H5a1 1 0 0 1-1-1z" />
        </svg>
      );
    }
    if (tabId === 'chat') {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 4h14a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H10l-4.8 3.9A.8.8 0 0 1 4 19.8v-3.3a2 2 0 0 1-1-1.7V6a2 2 0 0 1 2-2z" />
        </svg>
      );
    }
    if (tabId === 'memory') {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3h10a3 3 0 0 1 3 3v12.5a2.5 2.5 0 0 1-4.3 1.7L12 16.5l-3.7 3.7A2.5 2.5 0 0 1 4 18.5V6a3 3 0 0 1 3-3z" />
        </svg>
      );
    }
    if (tabId === 'work') {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5zM9 7a1 1 0 0 0-1 1v8h2v-3h4v3h2V8a1 1 0 0 0-1-1zm1 4V9h4v2z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7a3 3 0 0 1 3-3h3v3.2c0 .45.36.8.8.8H14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3zm8-3 4 4h-2.9a1.1 1.1 0 0 1-1.1-1.1zM7.5 12h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1 0-1.5zm0 3h5a.75.75 0 0 1 0 1.5h-5a.75.75 0 0 1 0-1.5z" />
      </svg>
    );
  };

  return (
    <>
      <div className="window">
        <div className={`topbar ${isMobileViewport ? 'topbar--mobile' : ''}`}>
          {!isMobileViewport ? <div className="dots"><span /><span /><span /></div> : null}
          <div className={`top-title ${isMobileViewport ? 'top-title--mobile' : ''}`}>{state.agent.name}</div>
          <span className={`status-chip ${isPausedByCredits ? 'error' : (effectiveIsThinking ? 'thinking' : 'idle')} ${isMobileViewport ? 'status-chip--mobile' : ''}`}>
            {isPausedByCredits ? t('agent.paused') : (effectiveIsThinking ? t('agent.thinking') : t('agent.idle'))}
          </span>
          <div className={`topbar-actions ${isMobileViewport ? 'topbar-actions--mobile' : ''}`}>
            {!isMobileViewport ? <span className="meta">{currentUser?.username || ''}</span> : null}
            <span className={`top-credit ${isPausedByCredits ? 'is-low' : ''}`} title={runtimeCreditsLabel}>
              {runtimeCreditsCompact}
            </span>
            <LanguagePicker className="locale-switch--top" />
            <Link
              to="/"
              className={`top-link ${isMobileViewport ? 'top-link--mobile' : ''}`}
              aria-label={t('common.back')}
              title={t('common.back')}
            >
              {isMobileViewport ? '←' : t('common.back')}
            </Link>
            <button
              type="button"
              className={isMobileViewport ? 'top-btn--mobile' : ''}
              onClick={onLogout}
              aria-label={t('common.logout')}
              title={t('common.logout')}
            >
              {isMobileViewport ? '⏻' : t('common.logout')}
            </button>
          </div>
        </div>

        {isPausedByCredits ? (
          <section className="card credit-warning">
            <p className="error">
              {t('credits.agentExhausted')}
            </p>
          </section>
        ) : null}

        <div className="layout" data-mobile-tab={mobileTab}>
        <aside className="left">
          <section className="panel panel-group-home">
            <div className="hdr"><div className="label">{t('agent.mission')}</div></div>
            <div className="cnt">
              <span className="meta">{t('common.lastRound', { value: formatDate(state.agent.last_round_at) })}</span>
              <span className={`meta agent-sync-state agent-sync-state--${agentStreamState}`}>
                {agentStreamState === 'live' ? t('agent.syncLive') : t('agent.syncFallback')}
              </span>
              <textarea
                value={objectiveDraft}
                onChange={(e) => {
                  objectiveDirtyRef.current = true;
                  setObjectiveDraft(e.target.value);
                }}
                rows={4}
                disabled={busy}
              />
              <div className="row-actions">
                <button onClick={updateObjective} disabled={busy || !objectiveDraft.trim()}>{t('common.save')}</button>
                <button onClick={triggerRound} disabled={busy}>{t('agent.triggerRound')}</button>
                <button
                  type="button"
                  onClick={() => setShowTemplatePublish((prev) => !prev)}
                  disabled={busy}
                >
                  {showTemplatePublish ? t('agent.hideTemplatePublish') : t('agent.publishTemplate')}
                </button>
              </div>
              {showTemplatePublish ? (
                <form onSubmit={publishTemplate} className="mini-form template-publish-form">
                  <label className="mini-form-label">
                    <span>{t('agent.templateVisibility')}</span>
                    <select
                      value={templatePublishForm.visibility}
                      onChange={(e) => setTemplatePublishForm((prev) => ({ ...prev, visibility: e.target.value }))}
                      disabled={busy}
                    >
                      <option value="public">{t('agent.templateVisibilityPublic')}</option>
                      <option value="private_link">{t('agent.templateVisibilityPrivate')}</option>
                    </select>
                  </label>
                  <input
                    type="text"
                    placeholder={t('agent.templateNameOptional')}
                    value={templatePublishForm.name}
                    onChange={(e) => setTemplatePublishForm((prev) => ({ ...prev, name: e.target.value }))}
                    disabled={busy}
                  />
                  <textarea
                    rows={2}
                    placeholder={t('agent.templateDescriptionOptional')}
                    value={templatePublishForm.description}
                    onChange={(e) => setTemplatePublishForm((prev) => ({ ...prev, description: e.target.value }))}
                    disabled={busy}
                  />
                  <div className="row-actions">
                    <button type="submit" disabled={busy}>{t('agent.publishTemplateSubmit')}</button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setShowTemplatePublish(false);
                        setTemplatePublishForm({
                          visibility: 'public',
                          name: '',
                          description: ''
                        });
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                  {templatePublishResult ? (
                    <div className="template-publish-result">
                      <span className="meta">{t('agent.templatePublished')}</span>
                      <span className="meta">
                        {t('common.status', { value: String(templatePublishResult.visibility || '-') })}
                      </span>
                      {templateShareUrl ? (
                        <div className="template-share-link-row">
                          <input type="text" readOnly value={templateShareUrl} />
                          <button type="button" onClick={copyTemplateShareLink}>{t('agent.copyLink')}</button>
                          <a href={templateShareUrl} target="_blank" rel="noreferrer">
                            <button type="button">{t('common.open')}</button>
                          </a>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </form>
              ) : null}
            </div>
          </section>

          <section className="panel panel-group-data">
            <div className="hdr">
              <div className="label">{t('agent.tools')}</div>
              <button type="button" className="icon-btn" onClick={toggleToolCreate} disabled={busy} title={t('agent.addTool')}>+</button>
            </div>
            <div className="cnt">
              {showToolCreate ? (
                <form onSubmit={addTool} className="mini-form">
                  <input
                    type="text"
                    placeholder={t('agent.toolNameOptional')}
                    value={toolForm.name}
                    onChange={(e) => setToolForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  <input
                    type="text"
                    placeholder={t('agent.toolServerPlaceholder')}
                    value={toolForm.mcp_server}
                    onChange={(e) => setToolForm((prev) => ({ ...prev, mcp_server: e.target.value }))}
                  />
                  {!isCaptainCode(toolForm.mcp_server.trim()) ? (
                    <>
                      <label className="mini-form-label">
                        <span>{t('agent.toolAuthTypeLabel')}</span>
                        <select
                          value={toolForm.auth_type}
                          onChange={(e) => setToolForm((prev) => ({ ...prev, auth_type: e.target.value }))}
                        >
                          <option value="none">{t('agent.toolAuthTypeNone')}</option>
                          <option value="bearer">{t('agent.toolAuthTypeBearer')}</option>
                          <option value="header">{t('agent.toolAuthTypeHeader')}</option>
                        </select>
                      </label>
                      {toolForm.auth_type === 'header' ? (
                        <input
                          type="text"
                          placeholder={t('agent.toolAuthHeaderPlaceholder')}
                          value={toolForm.auth_header_name}
                          onChange={(e) => setToolForm((prev) => ({ ...prev, auth_header_name: e.target.value }))}
                        />
                      ) : null}
                      {toolForm.auth_type !== 'none' ? (
                        <input
                          type="password"
                          placeholder={t('agent.toolAuthTokenPlaceholder')}
                          value={toolForm.auth_token}
                          onChange={(e) => setToolForm((prev) => ({ ...prev, auth_token: e.target.value }))}
                          autoComplete="off"
                        />
                      ) : null}
                    </>
                  ) : null}
                  <span className="meta">{t('agent.toolCaptainHint')}</span>
                  <div className="row-actions">
                    <button
                      type="submit"
                      disabled={
                        busy ||
                        !toolForm.mcp_server.trim() ||
                        (!isCaptainCode(toolForm.mcp_server.trim()) && !toolForm.name.trim()) ||
                        (
                          !isCaptainCode(toolForm.mcp_server.trim()) &&
                          toolForm.auth_type === 'header' &&
                          !toolForm.auth_header_name.trim()
                        ) ||
                        (
                          !isCaptainCode(toolForm.mcp_server.trim()) &&
                          toolForm.auth_type !== 'none' &&
                          !toolForm.auth_token.trim()
                        )
                      }
                    >
                      {t('common.add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowToolCreate(false);
                        setToolForm({
                          name: '',
                          mcp_server: '',
                          auth_type: 'none',
                          auth_header_name: '',
                          auth_token: ''
                        });
                      }}
                      disabled={busy}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              ) : null}
              <ul className="compact-list compact-list--tight">
                {visibleTools.map((tool) => (
                  <li key={tool.id} className="resource-row">
                    <div className="resource-main">
                      <span className={`resource-token ${toolTypeClassName(tool)}`.trim()}>
                        {toolTypeLabel(tool)}
                      </span>
                      <span className="resource-line" title={`${tool.name} (${toolServerLabel(tool, t)})`}>
                        <strong>{tool.name}</strong>
                        <span className="resource-sub">@{toolServerLabel(tool, t)}</span>
                      </span>
                    </div>
                    {tool.tool_source === 'internal' ? (
                      <span className="row-icon-badge" title={t('agent.nativeTool')}>SYS</span>
                    ) : (
                      <button
                        type="button"
                        className="row-icon-btn row-icon-btn--danger"
                        onClick={() => removeTool(tool.id)}
                        disabled={busy}
                        title={t('agent.removeTool')}
                        aria-label={t('agent.removeTool')}
                      >
                        x
                      </button>
                    )}
                  </li>
                ))}
                {visibleTools.length === 0 ? <li className="empty-row muted">{t('agent.noTools')}</li> : null}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-data">
            <div className="hdr">
              <div className="label">{t('agent.documents')}</div>
              <button type="button" className="icon-btn" onClick={toggleDocActions} disabled={busy} title={t('agent.addDocument')}>+</button>
              <input ref={uploadInputRef} type="file" className="hidden-upload" onChange={handleUploadDocument} />
            </div>
            <div className="cnt">
              {showDocActions ? (
                <div className="resource-actions">
                  <button type="button" onClick={openUploadDialog} disabled={busy}>{t('agent.uploadFile')}</button>
                  <button type="button" onClick={showDocLinkCreator} disabled={busy}>{t('agent.addLink')}</button>
                </div>
              ) : null}
              {showDocLinkForm ? (
                <form onSubmit={addDocument} className="mini-form">
                  <input
                    type="text"
                    placeholder={t('agent.documentNameOptional')}
                    value={docForm.name}
                    onChange={(e) => setDocForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  <input
                    type="text"
                    placeholder={t('agent.urlPlaceholder')}
                    value={docForm.url}
                    onChange={(e) => setDocForm((prev) => ({ ...prev, url: e.target.value }))}
                  />
                  <div className="row-actions">
                    <button type="submit" disabled={busy || !docForm.url.trim()}>{t('common.add')}</button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDocLinkForm(false);
                        setDocForm({ name: '', url: '' });
                      }}
                      disabled={busy}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              ) : null}
              <ul className="compact-list compact-list--tight">
                {(state.documents || []).map((doc) => (
                  <li key={doc.id} className="resource-row">
                    <div className="resource-main">
                      <span className={`resource-token resource-token--${normalizeDocumentType(doc)}`}>
                        {documentTypeToken(normalizeDocumentType(doc))}
                      </span>
                      <span className="resource-line" title={doc.name}>
                        <strong>{doc.name}</strong>
                      </span>
                    </div>
                    <div className="resource-icons">
                      <span className="row-icon-badge row-icon-badge--info" title={buildDocumentTooltip(doc, t)}>i</span>
                      {doc.download_path ? (
                        <button
                          type="button"
                          className="row-icon-btn"
                          onClick={() => {
                            const downloadUrl = buildDocumentDownloadUrl(doc, accessToken);
                            if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                          }}
                          title={t('agent.downloadDocument')}
                          aria-label={t('agent.downloadDocument')}
                        >
                          ↓
                        </button>
                      ) : null}
                      <span
                        className={`row-status-dot row-status-dot--${normalizeAnalysisStatus(doc.analysis_status)}`}
                        title={`${t('agent.documentAnalysisLabel')}: ${normalizeAnalysisStatus(doc.analysis_status)}`}
                      />
                      <button
                        type="button"
                        className="row-icon-btn row-icon-btn--danger"
                        onClick={() => removeDocument(doc.id)}
                        disabled={busy}
                        title={t('agent.removeDocument')}
                        aria-label={t('agent.removeDocument')}
                      >
                        x
                      </button>
                    </div>
                  </li>
                ))}
                {(state.documents || []).length === 0 ? <li className="empty-row muted">{t('agent.noDocuments')}</li> : null}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-data">
            <div className="hdr">
              <div className="label">{t('agent.captainWebApps')}</div>
            </div>
            <div className="cnt">
              <ul className="compact-list compact-list--tight">
                {(state.web_apps || []).map((app) => (
                  <li key={app.id} className="resource-row">
                    <div className="resource-main">
                      <span className="resource-token resource-token--native">WEB</span>
                      <span className="resource-line" title={app.name}>
                        <strong>{app.name}</strong>
                        <span className="resource-sub">{app.app_code}</span>
                      </span>
                    </div>
                    <span className="row-icon-badge">{(app.links || []).length}</span>
                  </li>
                ))}
                {(state.web_apps || []).length === 0 ? <li className="empty-row muted">{t('agent.noWebApps')}</li> : null}
              </ul>
              {(state.web_apps || []).map((app) => (
                <details key={`links-${app.id}`} className="captain-inner-details">
                  <summary>{t('agent.linksLabel', { name: app.name, count: (app.links || []).length })}</summary>
                  <ul className="compact-list compact-list--tight">
                    {(app.links || []).map((link) => {
                      const fullUrl = buildWebFormUrl(link.link_code);
                      return (
                        <li key={link.id} className="resource-row">
                          <div className="resource-main">
                            <span className="resource-token resource-token--native">URL</span>
                            <span className="resource-line" title={fullUrl}>
                              <strong>{link.link_code}</strong>
                              <span className="resource-sub">{fullUrl}</span>
                            </span>
                          </div>
                          <a href={fullUrl} target="_blank" rel="noreferrer">
                            <button type="button">{t('common.open')}</button>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                  {app.usage_instructions ? <p className="meta">{t('common.usage', { value: app.usage_instructions })}</p> : null}
                </details>
              ))}
            </div>
          </section>
        </aside>

        <main className="center">
          <section className="panel panel-group-chat">
            <div className="hdr">
              <div className="label">{t('agent.chat')}</div>
              <span className={`status-chip ${effectiveIsThinking ? 'thinking' : 'idle'}`}>
                {effectiveIsThinking ? (activeEventType || t('agent.chatProcessing')) : t('agent.chatReady')}
              </span>
            </div>
            <div className="cnt">
              <div className="chat-box" ref={chatBoxRef} onScroll={handleChatScroll}>
                {(state.chat_messages || []).map((msg) => (
                  <div key={msg.id} className={`chat-msg ${msg.role === 'assistant' ? 'assistant' : 'user'}`}>
                    <span>{msg.content}</span>
                  </div>
                ))}
                {isChatTyping ? (
                  <div className="chat-msg assistant typing-indicator" aria-live="polite">
                    <span className="typing-dots"><span /><span /><span /></span>
                    <span className="typing-text">{t('agent.chatTyping')}</span>
                  </div>
                ) : null}
              </div>
              {!isMobileChatActive ? (
                <form onSubmit={sendChat} className="chat-form">
                  <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder={t('agent.chatSendPlaceholder')} disabled={busy} />
                  <button type="submit" disabled={busy || !chatInput.trim()}>{t('agent.chatSend')}</button>
                </form>
              ) : null}
            </div>
          </section>

          <section className="panel panel-group-work">
            <div className="hdr"><div className="label">{t('agent.artifacts')}</div></div>
            <div className="cnt">
              {(state.artifacts || []).length === 0 ? (
                <p className="muted">{t('agent.noArtifacts')}</p>
              ) : (
                <div className="artifacts-grid">
                  {(state.artifacts || []).map((a) => {
                    const type = normalizeArtifactType(a.artifact_type);
                    const webFormArtifact = type === 'webform' ? parseWebFormArtifact(a.content) : null;
                    const displayTitle = type === 'webform'
                      ? (String(webFormArtifact?.title || '').trim() || String(a.title || '').trim() || 'Web Form')
                      : a.title;
                    const hasTableEditor = type === 'widget' && widgetArtifactHasTableEditor(a);
                    const cardClassName = [
                      'artifact-card',
                      `artifact-card--${type}`,
                      hasTableEditor ? 'artifact-card--widget-table' : ''
                    ].filter(Boolean).join(' ');
                    return (
                      <article key={a.id} className={cardClassName}>
                        <header>
                          <strong>{displayTitle}</strong>
                          <span className="meta">{type}</span>
                        </header>
                        {type === 'webform'
                          ? renderWebFormArtifact(a)
                          : (type === 'widget'
                            ? renderWidgetArtifact(a)
                            : renderStaticArtifactBody(a, state.documents || [], accessToken))}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </main>

        <aside className="right">
          <section className="panel panel-group-home panel-open-questions">
            <div className="hdr"><div className="label">{t('agent.openQuestions')}</div></div>
            <div className="cnt">
              {openQuestions.length === 0 ? <p className="muted">{t('agent.noOpenQuestions')}</p> : (
                <div className="stack">
                  {openQuestions.map((q) => {
                    const questionMode = resolveQuestionInputMode(q);
                    return (
                      <div key={q.id} className="sub-card">
                        <p><strong>{q.question_text}</strong></p>
                        <span className="meta">{t('agent.questionMeta', { id: q.id, type: questionTypeLabel(q, t) })}</span>
                        <div className="question-answer-form">
                          {questionMode === 'closed' ? (
                            <select value={questionDrafts[q.id] || ''} onChange={(e) => setQuestionDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}>
                              <option value="">{t('agent.selectOneAnswer')}</option>
                              {(q.options || []).map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                            </select>
                          ) : questionMode === 'multi_closed' ? (
                            <div className="multi-question-options">
                              {(q.options || []).map((opt) => {
                                const selected = Array.isArray(questionDrafts[q.id]) && questionDrafts[q.id].includes(opt);
                                return (
                                  <label key={opt} className="multi-question-option">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={(e) => toggleMultiQuestionOption(q.id, opt, e.target.checked)}
                                    />
                                    <span>{opt}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <input type="text" placeholder={t('agent.yourAnswer')} value={questionDrafts[q.id] || ''} onChange={(e) => setQuestionDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))} />
                          )}
                          <button className="question-submit-btn" onClick={() => answerQuestion(q)} disabled={busy || isQuestionDraftEmpty(q)}>{t('agent.submitAnswer')}</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="panel panel-group-home">
            <div className="hdr"><div className="label">{t('agent.listeners')}</div></div>
            <div className="cnt">
              <ul className="compact-list plain">
                {(state.listeners || []).map((l) => {
                  const toolInputTooltip = buildToolInputTooltip(l.tool_execution?.input);
                  return (
                    <li key={l.id}>
                      <div>
                        <strong>{l.listener_type}</strong>
                        <span className="meta">{t('common.reason', { value: l.payload?.reason || '-' })}</span>
                        {l.question_id ? <span className="meta">{t('agent.questionRef', { id: l.question_id })}</span> : null}
                        {l.tool_execution_id ? (
                          <div className="listener-tool-meta">
                            <span className="meta">
                              {t('agent.toolWait', {
                                name: l.tool_execution?.tool_name || `Tool #${l.tool_execution?.tool_id || '-'}`,
                                id: l.tool_execution_id
                              })}
                            </span>
                            {toolInputTooltip ? (
                              <span className="row-icon-badge row-icon-badge--info" title={toolInputTooltip}>
                                i
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <span className="meta">{t('common.trigger', { value: l.trigger_at ? formatDate(l.trigger_at) : '-' })}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-memory">
            <div className="hdr"><div className="label">{t('agent.memoryEvents')}</div></div>
            <div className="cnt">
              <ul className="compact-list plain">
                {(state.memory_events || []).map((ev) => (
                  <li key={ev.id} className="memory-event-row">
                    <div className="memory-event-body">
                      <strong>{ev.title}</strong>
                      <p className="objective-preview">{ev.details}</p>
                      <span className="meta">{ev.impact} · {formatDate(ev.created_at)}</span>
                    </div>
                    <button
                      type="button"
                      className="memory-remove-btn"
                      onClick={() => removeMemoryEvent(ev.id)}
                      disabled={busy}
                      aria-label={t('agent.removeMemoryEvent')}
                      title={t('agent.removeMemoryEvent')}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-home">
            <div className="hdr"><div className="label">{t('agent.toolRequests')}</div></div>
            <div className="cnt">
              <ul className="compact-list plain">
                {(state.tool_requests || []).map((req) => (
                  <li key={req.id}>
                    <div>
                      <strong>{req.tool_name}</strong>
                      <p className="objective-preview">{req.reason}</p>
                      <span className="meta">{t('agent.toolRequestMeta', { status: req.status, mode: req.fulfillment_mode })}</span>
                    </div>
                    {req.status === 'requested' ? (
                      <button onClick={() => resolveToolRequest(req.id, 'declined')} disabled={busy}>{t('agent.decline')}</button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-memory">
            <div className="hdr"><div className="label">{t('agent.recentEvents')}</div></div>
            <div className="cnt">
              <ul className="compact-list plain">
                {(state.events || []).map((ev) => (
                  <li key={ev.id}>
                    <div>
                      <strong>{ev.event_type}</strong>
                      <span className="meta">{ev.status} · {formatDate(ev.created_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="panel panel-group-memory">
            <div className="hdr">
              <div className="label">{t('agent.llmDebugLog')}</div>
              <button type="button" onClick={() => setShowLlmDebug((v) => !v)}>
                {showLlmDebug ? t('common.hide') : t('common.show')}
              </button>
            </div>
            {showLlmDebug ? (
              <div className="cnt">
                {(state.llm_calls || []).length === 0 ? (
                  <p className="muted">{t('agent.noLlmCalls')}</p>
                ) : (
                  <ul className="llm-log-list">
                    {(state.llm_calls || []).map((item) => (
                      <li key={item.id} className={`llm-log-row llm-log-row--${item.status || 'ok'}`}>
                        <span className="llm-log-time">{formatDate(item.created_at)}</span>
                        <span className="llm-log-main" title={item.trigger || ''}>
                          {llmCallTypeLabel(item.call_type, t)} · {truncateText(item.trigger, 220)}
                        </span>
                        {item.model ? <span className="llm-log-meta">{item.model}</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </section>
        </aside>
      </div>

      {error && <p className="error card">{error}</p>}
      </div>

      {isMobileChatActive && typeof document !== 'undefined'
        ? createPortal(
          <form onSubmit={sendChat} className="chat-form chat-form-mobile-overlay">
            <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder={t('agent.chatSendPlaceholder')} disabled={busy} />
            <button type="submit" disabled={busy || !chatInput.trim()}>{t('agent.chatSend')}</button>
          </form>,
          document.body
        )
        : null}

      {isMobileViewport ? (
        <nav className="mobile-bottom-nav" aria-label={t('agent.mobileNav')}>
          {MOBILE_TABS.map((tab) => {
            const badge = formatUnreadBadge(unreadTabs[tab.id]);
            const active = mobileTab === tab.id;
            const label = tab.id === 'home'
              ? t('tabs.focus')
              : tab.id === 'chat'
                ? t('tabs.chat')
                : tab.id === 'memory'
                  ? t('tabs.memory')
                  : tab.id === 'work'
                    ? t('tabs.work')
                    : t('tabs.data');
            return (
              <button
                key={tab.id}
                type="button"
                className={`mobile-bottom-tab ${active ? 'active' : ''}`}
                onClick={() => {
                  setMobileTab(tab.id);
                  markSeenChannels(MOBILE_TAB_CHANNELS[tab.id] || []);
                }}
              >
                <span className="mobile-bottom-tab-icon">{renderMobileTabIcon(tab.id)}</span>
                <span className="mobile-bottom-tab-label">{label}</span>
                {badge ? <span className="mobile-bottom-tab-badge">{badge}</span> : null}
              </button>
            );
          })}
        </nav>
      ) : null}
    </>
  );
}

function LoginPage({ onAuthenticated }) {
  const { t } = useI18n();
  const [authMode, setAuthMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleButtonRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    try {
      setLoading(true);
      setError('');
      const endpoint = authMode === 'register' ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API_URL}${endpoint}`, {
        username: username.trim(),
        password
      });
      const payload = response?.data?.data || null;
      if (!payload?.access_token || !payload?.user) {
        throw new Error(t('auth.invalidLoginResponse'));
      }
      onAuthenticated(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const submitGoogleIdToken = useCallback(async (idToken) => {
    const token = String(idToken || '').trim();
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(`${API_URL}/auth/google`, {
        id_token: token
      });
      const payload = response?.data?.data || null;
      if (!payload?.access_token || !payload?.user) {
        throw new Error(t('auth.invalidGoogleResponse'));
      }
      onAuthenticated(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  }, [onAuthenticated, t]);

  useEffect(() => {
    const clientId = String(GOOGLE_CLIENT_ID || '').trim();
    if (!clientId) return undefined;

    let cancelled = false;
    const isIOSWebKit = (() => {
      if (typeof navigator === 'undefined') return false;
      const ua = String(navigator.userAgent || '');
      const platform = String(navigator.platform || '');
      const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
      return /iPad|iPhone|iPod/i.test(ua) || (/Mac/i.test(platform) && maxTouchPoints > 1);
    })();

    const renderGoogleButton = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;

      const initializeConfig = {
        client_id: clientId,
        use_fedcm_for_prompt: false
      };
      if (isIOSWebKit) {
        initializeConfig.ux_mode = 'redirect';
        initializeConfig.login_uri = `${window.location.origin}/api/auth/google/redirect`;
      } else {
        initializeConfig.ux_mode = 'popup';
        initializeConfig.itp_support = true;
        initializeConfig.callback = (response) => {
          if (response?.credential) {
            submitGoogleIdToken(response.credential);
            return;
          }
          setError(t('auth.googleFailed'));
        };
      }
      window.google.accounts.id.initialize(initializeConfig);

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 300
      });
    };

    const ensureScriptAndRender = async () => {
      if (window.google?.accounts?.id) {
        renderGoogleButton();
        return;
      }
      const existing = document.getElementById('google-gsi-client');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'google-gsi-client';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = renderGoogleButton;
        script.onerror = () => {
          if (!cancelled) setError(t('auth.googleUnavailable'));
        };
        document.head.appendChild(script);
        return;
      }
      if (window.google?.accounts?.id) {
        renderGoogleButton();
        return;
      }
      existing.addEventListener('load', renderGoogleButton, { once: true });
    };

    ensureScriptAndRender();
    return () => {
      cancelled = true;
    };
  }, [submitGoogleIdToken, t]);

  const isRegisterMode = authMode === 'register';

  return (
    <div className="window">
      <div className="topbar">
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('auth.title')}</div>
        <div className="topbar-actions">
          <LanguagePicker className="locale-switch--top" />
        </div>
      </div>
      <div className="page">
        <header className="hero">
          <h1>{isRegisterMode ? t('auth.createAccount') : t('auth.signIn')}</h1>
          <p>
            {isRegisterMode
              ? t('auth.registerLead')
              : t('auth.loginLead')}
          </p>
        </header>
        <section className="card auth-card">
          <div className="auth-mode-switch" role="tablist" aria-label={t('auth.modeLabel')}>
            <button
              type="button"
              className={authMode === 'login' ? 'active' : ''}
              onClick={() => {
                setAuthMode('login');
                setError('');
              }}
              disabled={loading}
            >
              {t('auth.signIn')}
            </button>
            <button
              type="button"
              className={authMode === 'register' ? 'active' : ''}
              onClick={() => {
                setAuthMode('register');
                setError('');
              }}
              disabled={loading}
            >
              {t('auth.register')}
            </button>
          </div>
          <form onSubmit={submit} className="agent-form">
            <input
              type="text"
              placeholder={t('auth.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button type="submit" disabled={loading || !username.trim() || !password}>
              {loading
                ? (isRegisterMode ? t('auth.creatingAccount') : t('auth.signingIn'))
                : (isRegisterMode ? t('auth.createAccount') : t('auth.signIn'))}
            </button>
          </form>
          {String(GOOGLE_CLIENT_ID || '').trim() ? (
            <div className="oauth-block">
              <div className="oauth-separator">
                <span>{t('common.or')}</span>
              </div>
              <div ref={googleButtonRef} className="google-login-anchor" />
            </div>
          ) : null}
          {error ? <p className="error">{error}</p> : null}
        </section>
      </div>
    </div>
  );
}

function AdminConsole({ currentUser, onLogout }) {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [costOverview, setCostOverview] = useState(null);
  const [registrationPolicy, setRegistrationPolicy] = useState(null);
  const [registrationLimitDraft, setRegistrationLimitDraft] = useState('50');
  const [geminiSettings, setGeminiSettings] = useState(null);
  const [geminiModelDraft, setGeminiModelDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  ));
  const [mobileTab, setMobileTab] = useState('users');
  const [createForm, setCreateForm] = useState({ username: '', password: '', is_admin: false });
  const [resetDrafts, setResetDrafts] = useState({});
  const [creditDrafts, setCreditDrafts] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResponse, activityResponse, costResponse, settingsResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/agents/activity`),
        axios.get(`${API_URL}/admin/llm/costs`),
        axios.get(`${API_URL}/admin/settings`)
      ]);
      setUsers(usersResponse?.data?.data || []);
      setActivity(activityResponse?.data?.data || []);
      setCostOverview(costResponse?.data?.data || null);
      const registration = settingsResponse?.data?.data?.registration || null;
      const gemini = settingsResponse?.data?.data?.gemini || null;
      setRegistrationPolicy(registration);
      setGeminiSettings(gemini);
      if (registration && Number.isFinite(Number(registration.max_users))) {
        setRegistrationLimitDraft(String(registration.max_users));
      }
      if (gemini && String(gemini.default_model || '').trim()) {
        setGeminiModelDraft(String(gemini.default_model));
      }
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCreditDrafts((previous) => {
      const next = {};
      (users || []).forEach((user) => {
        const current = previous[user.id];
        next[user.id] = {
          credits_remaining: current?.credits_remaining ?? String(user.credits_remaining ?? 0),
          credits_unlimited: current?.credits_unlimited ?? Boolean(user.credits_unlimited)
        };
      });
      return next;
    });
  }, [users]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!createForm.username.trim() || !createForm.password) return;
    try {
      setLoading(true);
      await axios.post(`${API_URL}/admin/users`, {
        username: createForm.username.trim(),
        password: createForm.password,
        is_admin: Boolean(createForm.is_admin)
      });
      setCreateForm({ username: '', password: '', is_admin: false });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const toggleAdmin = async (user) => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/admin/users/${user.id}/admin`, {
        is_admin: !Boolean(user.is_admin)
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/admin/users/${user.id}/active`, {
        is_active: !Boolean(user.is_active)
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const resetPassword = async (userId) => {
    const draft = String(resetDrafts[userId] || '');
    if (!draft) return;
    try {
      setLoading(true);
      await axios.post(`${API_URL}/admin/users/${userId}/reset-password`, {
        new_password: draft
      });
      setResetDrafts((prev) => ({ ...prev, [userId]: '' }));
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const updateRegistrationLimit = async (e) => {
    e.preventDefault();
    const limit = Number(registrationLimitDraft);
    if (!Number.isFinite(limit) || limit < 1) return;
    try {
      setLoading(true);
      const response = await axios.patch(`${API_URL}/admin/settings/registration`, {
        max_users: Math.floor(limit)
      });
      const policy = response?.data?.data || null;
      if (policy) {
        setRegistrationPolicy(policy);
        setRegistrationLimitDraft(String(policy.max_users));
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const updateGeminiModel = async (e) => {
    e.preventDefault();
    const model = String(geminiModelDraft || '').trim();
    if (!model) return;
    try {
      setLoading(true);
      const response = await axios.patch(`${API_URL}/admin/settings/gemini-model`, { model });
      const payload = response?.data?.data || null;
      if (payload) {
        setGeminiSettings(payload);
        if (String(payload.default_model || '').trim()) {
          setGeminiModelDraft(String(payload.default_model));
        }
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const saveUserCredits = async (userId) => {
    const draft = creditDrafts[userId];
    if (!draft) return;
    const parsedRemaining = Number(draft.credits_remaining);
    if (!Number.isFinite(parsedRemaining) || parsedRemaining < 0) {
      setError(t('credits.mustBeNonNegative'));
      return;
    }
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/admin/users/${userId}/credits`, {
        credits_remaining: Math.floor(parsedRemaining),
        credits_unlimited: Boolean(draft.credits_unlimited)
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const deleteAgent = async (agentId) => {
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/admin/agents/${agentId}`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const costPeriods = costOverview?.periods || {};
  const costCards = [
    { key: 'day', label: t('admin.day') },
    { key: 'week', label: t('admin.week') },
    { key: 'month', label: t('admin.month') }
  ];
  const pricingRows = Array.isArray(costOverview?.pricing)
    ? costOverview.pricing.filter((row) => row.model_family !== 'other')
    : [];
  const geminiOptions = Array.isArray(geminiSettings?.options) ? geminiSettings.options : [];

  return (
    <div className="window">
      <div className={`topbar ${isMobileViewport ? 'topbar--mobile-compact' : ''}`}>
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('admin.title')}</div>
        <div className={`topbar-actions ${isMobileViewport ? 'topbar-actions--mobile' : ''}`}>
          {!isMobileViewport ? <span className="meta">{currentUser?.username || ''}</span> : null}
          <span className="top-credit" title={userCreditsLabel(currentUser, t)}>
            {compactCreditsLabel(currentUser, t)}
          </span>
          <LanguagePicker className="locale-switch--top" />
          <Link
            to="/"
            className={`top-link ${isMobileViewport ? 'top-link--mobile' : ''}`}
            aria-label={t('common.back')}
            title={t('common.back')}
          >
            {isMobileViewport ? '←' : t('common.back')}
          </Link>
          <button
            type="button"
            className={isMobileViewport ? 'top-btn--mobile' : ''}
            onClick={onLogout}
            aria-label={t('common.logout')}
            title={t('common.logout')}
          >
            {isMobileViewport ? '⏻' : t('common.logout')}
          </button>
        </div>
      </div>

      <div className="page">
        <header className="hero">
          <h1 className="visually-hidden">{t('admin.title')}</h1>
          <p className="hero-kicker">{t('admin.heroKicker')}</p>
          <p className="hero-lead">{t('admin.heroLead')}</p>
        </header>
        {isMobileViewport ? (
          <div className="mobile-section-tabs">
            <button
              type="button"
              className={`mobile-section-tab ${mobileTab === 'users' ? 'active' : ''}`}
              onClick={() => setMobileTab('users')}
            >
              {t('tabs.users')}
            </button>
            <button
              type="button"
              className={`mobile-section-tab ${mobileTab === 'activity' ? 'active' : ''}`}
              onClick={() => setMobileTab('activity')}
            >
              {t('tabs.activity')}
            </button>
          </div>
        ) : null}

        {(!isMobileViewport || mobileTab === 'users') ? (
        <section className="card admin-section admin-section--users">
          <div className="card-head">
            <h2>{t('admin.createUser')}</h2>
            <button
              type="button"
              className="agents-action-btn admin-refresh-btn"
              onClick={loadData}
              disabled={loading}
              aria-label={t('admin.refreshAdminData')}
              title={t('admin.refreshAdminData')}
            >
              <span className="btn-icon" aria-hidden="true">↻</span>
              <span className="btn-label">{t('common.refresh')}</span>
            </button>
          </div>
          <form onSubmit={updateRegistrationLimit} className="agent-form admin-registration-form">
            <label>
              {t('admin.registrationMaxUsers')}
              <input
                type="number"
                min="1"
                value={registrationLimitDraft}
                onChange={(e) => setRegistrationLimitDraft(e.target.value)}
                disabled={loading}
              />
            </label>
            <button
              type="submit"
              disabled={loading || !String(registrationLimitDraft || '').trim()}
            >
              {t('admin.saveRegistrationLimit')}
            </button>
            <span className="meta">
              {t('admin.registrationCurrent', {
                current: registrationPolicy?.registered_users ?? 0,
                max: registrationPolicy?.max_users ?? '-',
                status: registrationPolicy?.is_open ? t('admin.registrationOpen') : t('admin.registrationClosed')
              })}
            </span>
          </form>
          <form onSubmit={updateGeminiModel} className="agent-form admin-gemini-form">
            <label>
              {t('admin.geminiModel')}
              <select
                value={geminiModelDraft}
                onChange={(e) => setGeminiModelDraft(e.target.value)}
                disabled={loading || !geminiOptions.length}
              >
                {geminiOptions.map((option) => (
                  <option key={option.model} value={option.model}>
                    {option.label || option.model}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" disabled={loading || !String(geminiModelDraft || '').trim()}>
              {t('admin.saveGeminiModel')}
            </button>
            <span className="meta">
              {t('admin.geminiModelCurrent', {
                current: geminiSettings?.default_model || '-',
                fallback: geminiSettings?.fallback_model || '-'
              })}
            </span>
          </form>
          <form onSubmit={createUser} className="agent-form admin-create-user-form">
            <input
              type="text"
              placeholder={t('auth.username')}
              value={createForm.username}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))}
              disabled={loading}
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={createForm.password}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
              disabled={loading}
            />
            <label className="inline-check inline-check--row">
              <input
                type="checkbox"
                checked={Boolean(createForm.is_admin)}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, is_admin: e.target.checked }))}
                disabled={loading}
              />
              <span>{t('admin.adminRights')}</span>
            </label>
            <button type="submit" disabled={loading || !createForm.username.trim() || !createForm.password}>
              {t('admin.createAccount')}
            </button>
          </form>
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'users') ? (
        <section className="card admin-section admin-section--users">
          <h2>{t('admin.accounts')}</h2>
          {users.length === 0 ? (
            <p className="muted">{t('admin.noUserFound')}</p>
          ) : (
            <ul className="agent-list">
              {users.map((user) => (
                <li key={user.id} className="agent-row admin-user-row">
                  <div className="admin-user-main">
                    <div className="admin-user-main-top">
                      <strong>{user.username}</strong>
                      <span className={`status-chip admin-status-chip ${user.is_active ? 'ready' : 'error'}`}>
                        {user.is_active ? t('admin.active') : t('admin.inactive')}
                      </span>
                    </div>
                    <div className="admin-user-main-meta">
                      <span className="meta">{t('common.role', { value: user.is_admin ? t('admin.adminRole') : t('admin.userRole') })}</span>
                      <span className="meta">
                        {t('credits.remaining', {
                          value: user.credits_unlimited ? t('admin.unlimited').toLowerCase() : formatCredits(user.credits_remaining)
                        })}
                        {user.credits_exhausted ? ` (${t('credits.exhausted')})` : ''}
                      </span>
                      <span className="meta">{t('common.lastLogin', { value: formatDate(user.last_login_at) })}</span>
                    </div>
                  </div>
                  <div className="admin-user-controls">
                    <div className="admin-user-control-line admin-user-control-line--access">
                      <button type="button" onClick={() => toggleActive(user)} disabled={loading}>
                        {user.is_active ? t('admin.deactivate') : t('admin.activate')}
                      </button>
                      <button type="button" onClick={() => toggleAdmin(user)} disabled={loading}>
                        {user.is_admin ? t('admin.removeAdmin') : t('admin.makeAdmin')}
                      </button>
                    </div>

                    <div className="admin-user-control-group">
                      <span className="meta admin-control-label">{t('admin.creditsLabel')}</span>
                      <div className="admin-user-control-line">
                        <input
                          type="number"
                          min="0"
                          placeholder={t('admin.creditsLabel')}
                          value={creditDrafts[user.id]?.credits_remaining ?? String(user.credits_remaining ?? 0)}
                          onChange={(e) => setCreditDrafts((prev) => ({
                            ...prev,
                            [user.id]: {
                              credits_remaining: e.target.value,
                              credits_unlimited: Boolean(prev[user.id]?.credits_unlimited ?? user.credits_unlimited)
                            }
                          }))}
                          disabled={loading}
                        />
                        <label className="inline-check">
                          <input
                            type="checkbox"
                            checked={Boolean(creditDrafts[user.id]?.credits_unlimited ?? user.credits_unlimited)}
                            onChange={(e) => setCreditDrafts((prev) => ({
                              ...prev,
                              [user.id]: {
                                credits_remaining: prev[user.id]?.credits_remaining ?? String(user.credits_remaining ?? 0),
                                credits_unlimited: e.target.checked
                              }
                            }))}
                            disabled={loading}
                          />
                          <span>{t('admin.unlimited')}</span>
                        </label>
                        <button type="button" onClick={() => saveUserCredits(user.id)} disabled={loading}>
                          {t('admin.saveCredits')}
                        </button>
                      </div>
                    </div>

                    <div className="admin-user-control-group">
                      <span className="meta admin-control-label">{t('admin.passwordLabel')}</span>
                      <div className="admin-user-control-line">
                        <input
                          type="password"
                          placeholder={t('admin.newPassword')}
                          value={resetDrafts[user.id] || ''}
                          onChange={(e) => setResetDrafts((prev) => ({ ...prev, [user.id]: e.target.value }))}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => resetPassword(user.id)}
                          disabled={loading || !String(resetDrafts[user.id] || '').trim()}
                        >
                          {t('admin.resetPassword')}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        ) : null}

        {(!isMobileViewport || mobileTab === 'activity') ? (
        <section className="card admin-section admin-section--activity">
          <h2>{t('admin.agentsActivity')}</h2>
          <div className="admin-cost-grid">
            {costCards.map((item) => {
              const period = costPeriods[item.key] || {};
              const totals = period.totals || {};
              return (
                <div key={item.key} className="admin-cost-card">
                  <span className="meta">{item.label}</span>
                  <strong>{formatUsd(totals.estimated_cost_usd || 0)}</strong>
                  <span className="meta">
                    {t('admin.callsInOut', {
                      calls: formatTokenCount(totals.llm_calls || 0),
                      input: formatTokenCount(totals.prompt_tokens || 0),
                      output: formatTokenCount(totals.completion_tokens || 0)
                    })}
                  </span>
                </div>
              );
            })}
          </div>
          {pricingRows.length > 0 ? (
            <details className="admin-cost-pricing">
              <summary>{t('admin.pricingConfiguration')}</summary>
              <ul className="compact-list plain">
                {pricingRows.map((row) => (
                  <li key={row.model_family}>
                    <div>
                      <strong>{row.label}</strong>
                      <span className="meta">
                        {t('admin.inputPer1m', { value: formatUsd(row.input_usd_per_1m || 0) })} · {t('admin.outputPer1m', { value: formatUsd(row.output_usd_per_1m || 0) })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {costCards.map((item) => {
            const period = costPeriods[item.key];
            const byModel = Array.isArray(period?.by_model) ? period.by_model : [];
            if (!byModel.length) return null;
            return (
              <details key={`model-${item.key}`} className="admin-cost-pricing">
                <summary>{t('admin.modelSplit', { period: item.label })}</summary>
                <ul className="compact-list plain">
                  {byModel.map((row) => (
                    <li key={`${item.key}-${row.model_family}`}>
                      <div>
                        <strong>{row.label || row.model_family}</strong>
                        <span className="meta">
                          {t('admin.costCallsInOut', {
                            cost: formatUsd(row.estimated_cost_usd || 0),
                            calls: formatTokenCount(row.llm_calls || 0),
                            input: formatTokenCount(row.prompt_tokens || 0),
                            output: formatTokenCount(row.completion_tokens || 0)
                          })}
                        </span>
                        {Array.isArray(row.models_seen) && row.models_seen.length ? (
                          <span className="meta">{t('admin.modelsSeen', { models: row.models_seen.join(', ') })}</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
          {activity.length === 0 ? (
            <p className="muted">{t('admin.noActivity')}</p>
          ) : (
            <div className="stack">
              {activity.map((bucket, index) => (
                <div key={`${bucket?.user?.id || 'unassigned'}-${index}`} className="sub-card">
                  <p>
                    <strong>{bucket.user ? bucket.user.username : t('admin.unassignedAgents')}</strong>
                  </p>
                  <span className="meta">
                    {t('admin.callsTokensCost', {
                      calls: bucket.totals?.llm_calls || 0,
                      tokens: bucket.totals?.total_tokens || 0,
                      cost: formatUsd(bucket.totals?.estimated_cost_usd || 0)
                    })}
                  </span>
                  <ul className="compact-list plain admin-agent-list">
                    {(bucket.agents || []).map((agent) => (
                      <li key={agent.agent_id}>
                        <div>
                          <strong>{agent.agent_name}</strong>
                          <span className="meta">
                            {t('admin.agentTotals', {
                              calls: agent.llm_calls,
                              prompt: agent.prompt_tokens,
                              completion: agent.completion_tokens,
                              total: agent.total_tokens,
                              cost: formatUsd(agent.estimated_cost_usd || 0)
                            })}
                          </span>
                          <span className="meta">{t('common.lastRound', { value: formatDate(agent.last_round_at) })}</span>
                        </div>
                        {agent.is_deleted ? null : (
                          <button type="button" className="danger-btn" onClick={() => deleteAgent(agent.agent_id)} disabled={loading}>
                            {t('admin.deleteAgent')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          {error ? <p className="error">{error}</p> : null}
        </section>
        ) : null}
      </div>
    </div>
  );
}

function SharedTemplateCreatePage({ currentUser, onLogout }) {
  const { t } = useI18n();
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');
  const [systemInstructionsDraft, setSystemInstructionsDraft] = useState(emptyAgentSystemInstructions());
  const [showAdvancedCreate, setShowAdvancedCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/templates/shared/${encodeURIComponent(shareCode || '')}`);
      const payload = response?.data?.data || null;
      setTemplate(payload);
      const templatePayload = (payload?.template && typeof payload.template === 'object') ? payload.template : {};
      const suggestedName = String(templatePayload.agent_name || payload?.name || '').trim();
      const suggestedObjective = String(templatePayload.objective || '').trim();
      setName(suggestedName);
      setObjective(suggestedObjective);
      setSystemInstructionsDraft(instructionsPayloadToDraft(templatePayload.system_instructions));
      setShowAdvancedCreate(Boolean(templatePayload.system_instructions));
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const createFromTemplate = async (e) => {
    e.preventDefault();
    if (!objective.trim()) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/templates/shared/${encodeURIComponent(shareCode || '')}/create-agent`,
        {
          name: name.trim(),
          objective: objective.trim(),
          system_instructions: systemInstructionsDraftToPayload(systemInstructionsDraft)
        }
      );
      const created = response?.data?.data || null;
      const createdId = Number(created?.id || 0);
      if (Number.isFinite(createdId) && createdId > 0) {
        navigate(`/agents/${createdId}`);
        return;
      }
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="window">
      <div className="topbar">
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('sharedTemplate.title')}</div>
        <div className="topbar-actions topbar-actions--home">
          <span className="meta">{currentUser?.username || ''}</span>
          <span className="top-credit" title={userCreditsLabel(currentUser, t)}>
            {compactCreditsLabel(currentUser, t)}
          </span>
          <LanguagePicker className="locale-switch--top" />
          <Link to="/" className="home-nav-btn" aria-label={t('common.back')} title={t('common.back')}>
            <span className="btn-icon" aria-hidden="true">←</span>
            <span className="btn-label">{t('common.back')}</span>
          </Link>
          <button type="button" className="home-nav-btn" onClick={onLogout} aria-label={t('common.logout')}>
            <span className="btn-icon" aria-hidden="true">⏻</span>
            <span className="btn-label">{t('common.logout')}</span>
          </button>
        </div>
      </div>
      <div className="page">
        <header className="hero">
          <h1 className="visually-hidden">{t('sharedTemplate.title')}</h1>
          <p className="hero-kicker">{t('sharedTemplate.heroKicker')}</p>
          <p className="hero-lead">{t('sharedTemplate.heroLead')}</p>
        </header>
        <section className="card">
          {template ? (
            <div className="shared-template-meta">
              <strong>{template.name}</strong>
              {template.description ? <p className="objective-preview">{template.description}</p> : null}
              <span className="meta">{t('home.templateBy', { owner: template.owner_username || '-' })}</span>
            </div>
          ) : null}
          <form onSubmit={createFromTemplate} className="agent-form shared-template-form">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('home.agentNamePlaceholder')}
              disabled={loading}
            />
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder={t('home.objectivePlaceholder')}
              disabled={loading}
              rows={3}
            />
            <button
              type="button"
              onClick={() => setShowAdvancedCreate((prev) => !prev)}
              disabled={loading}
            >
              {showAdvancedCreate ? t('home.hideAdvancedInstructions') : t('home.showAdvancedInstructions')}
            </button>
            {showAdvancedCreate ? (
              <AgentSystemInstructionsFields
                draft={systemInstructionsDraft}
                setDraft={setSystemInstructionsDraft}
                disabled={loading}
                t={t}
                className="advanced-create-grid"
              />
            ) : null}
            <button type="submit" disabled={loading || !objective.trim()}>
              {loading ? t('common.saving') : t('sharedTemplate.createCta')}
            </button>
          </form>
          {error ? <p className="error">{error}</p> : null}
        </section>
      </div>
    </div>
  );
}

function PublicWebFormRedirect() {
  const { t } = useI18n();
  const { linkCode } = useParams();
  const target = useMemo(() => buildWebFormUrl(linkCode), [linkCode]);

  useEffect(() => {
    if (!target) return;
    window.location.replace(target);
  }, [target]);

  return (
    <div className="window">
      <div className="topbar">
        <div className="dots"><span /><span /><span /></div>
        <div className="top-title">{t('publicWebForm.title')}</div>
        <div className="topbar-actions">
          <LanguagePicker className="locale-switch--top" />
        </div>
      </div>
      <div className="page">
        <header className="hero">
          <h1>{t('publicWebForm.opening')}</h1>
          <p>{target || t('publicWebForm.invalid')}</p>
        </header>
      </div>
    </div>
  );
}

function App() {
  const { t } = useI18n();
  const [accessToken, setAccessToken] = useState(getStoredAccessToken());
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  const applyAuthenticatedState = useCallback((payload) => {
    const token = payload?.access_token || '';
    const user = payload?.user || null;
    storeAccessToken(token);
    applyAxiosToken(token);
    setAccessToken(token);
    setCurrentUser(user);
    setSessionLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch {
      // Best effort logout on server side.
    }
    storeAccessToken('');
    applyAxiosToken('');
    setAccessToken('');
    setCurrentUser(null);
    setSessionLoading(false);
  }, []);

  useEffect(() => {
    applyAxiosToken(accessToken);
  }, [accessToken]);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const token = getStoredAccessToken();
      if (!token) {
        if (!cancelled) {
          setCurrentUser(null);
          setSessionLoading(false);
        }
        return;
      }
      applyAxiosToken(token);
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        if (cancelled) return;
        setAccessToken(token);
        setCurrentUser(response?.data?.data || null);
      } catch {
        if (cancelled) return;
        storeAccessToken('');
        applyAxiosToken('');
        setAccessToken('');
        setCurrentUser(null);
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  if (sessionLoading) {
    return (
      <div className="window">
        <div className="topbar">
          <div className="dots"><span /><span /><span /></div>
          <div className="top-title">{t('common.appName')}</div>
        </div>
        <div className="page">
          <header className="hero">
            <h1>{t('common.loadingSession')}</h1>
          </header>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/webform/:linkCode" element={<PublicWebFormRedirect />} />
        {!currentUser ? (
          <>
            <Route path="*" element={<LoginPage onAuthenticated={applyAuthenticatedState} />} />
          </>
        ) : (
          <>
            <Route path="/" element={<AgentsHome currentUser={currentUser} onLogout={logout} />} />
            <Route
              path="/captain"
              element={
                currentUser.is_admin
                  ? <CaptainConsole currentUser={currentUser} accessToken={accessToken} onLogout={logout} />
                  : <Navigate to="/" replace />
              }
            />
            <Route
              path="/admin"
              element={currentUser.is_admin ? <AdminConsole currentUser={currentUser} onLogout={logout} /> : <Navigate to="/" replace />}
            />
            <Route
              path="/templates/shared/:shareCode"
              element={<SharedTemplateCreatePage currentUser={currentUser} onLogout={logout} />}
            />
            <Route
              path="/agents/:agentId"
              element={<AgentWorkspace currentUser={currentUser} accessToken={accessToken} onLogout={logout} />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
